import { StorageImage } from "@/components/StorageImage";
import { useToast } from "@/hooks/use-toast";
import { isReviewPriority } from "@shared/relabelReview";
import { AnnotatedImageViewer, type AnnotatedImageViewerHandle } from "@/components/AnnotatedImageViewer";
import TransitionOverlay from "@/components/TransitionOverlay";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getTraitColor, getTraitIcon } from "@/lib/traitIcons";
import { cn } from "@/lib/utils";
import { EntryCollection } from "@/types/entryCollection";
import {
  EntryConfig,
  ENTRY_CONFIG_TYPES,
  IMAGE_MEASUREMENT_MODEL_KINDS,
  ImageMeasurementModelKind,
  isKeypointModel,
} from "@/types/entryConfig";
import { Plot } from "@/types/plot";
import { AnnotationDisplayOptions, DEFAULT_DISPLAY_OPTIONS } from "@/types/annotationDisplayOptions";
import { getEntryDisplayValue } from "@/utils/entryCollectionUtils";
import { buildNestedSampleFieldUpdates } from "@/utils/entryFieldUpdateUtils";
import {
  EntryImage,
  extractImagesFromEntryCollection,
  formatEntryValue,
  getFormattedEntries,
  getTranscriptionSamplesForPlot,
  formatTimestampReadable,
  invalidateAnnotationMetadataCache,
} from "@/utils/entryUtils";
import { getTraitStats } from "@/utils/traitStatsUtils";
import {
  applyAdditionalMeasurementAvgMetrics,
  buildAdditionalMeasurementAvgFieldUpdates,
  resolveMeasurementAxisLabel,
  type AdditionalMeasurementAvgMetrics,
} from "@/utils/annotationMeasurementUtils";
import {
  describeChangedViewSettings,
  shouldAskToPersistViewSettings,
  type AnnotationViewSettings,
  type ObjectReviewSession,
  type ViewSettingsSession,
} from "@/components/Annotations/annotationViewSettings";
import { supportsOutsideBoundaryAnnotationsForEntryConfig } from "@/components/Annotations/outsideBoundaryEntryConfig";
import { PersistViewSettingsDialog } from "@/components/PersistViewSettingsDialog";
import { ReviewObjectsCompleteDialog } from "@/components/ReviewObjectsCompleteDialog";
import { resolveReviewArrowOutcome, type ReviewLandingEdge } from "@/utils/reviewObjectNavigation";
import {
  buildPlotImageSequence,
  orderEditableReviewQueue,
  getEditablePlotImageKey,
  isEditableAnnotationImage,
  isSequenceImage,
  resolveEditableImageNavigationTarget,
  shouldIgnoreSequenceNavigationKeys,
  type EditablePlotImageSequenceItem,
  type SequenceDirection,
} from "@/utils/plotImageSequence";
import {
  canEditRenderedAnnotationImage,
  getDeferredAnalysisEntryMapFieldForImageType,
  isDeferredAnalysisReviewedEntry,
  isDeferredAnalysisUnreviewedEntry,
  shouldRenderAnnotatedImage,
} from "@/utils/deferredAnalysisUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ANNOTATION_TYPES, type AnnotationType } from "@/components/Annotations/types";
import { getDescriptorsForEntryConfig } from "@shared/types/measurementDescriptors";
import { resolveContainmentParams, type ContainmentParams } from "@shared/containmentOverlapFilter";
import {
  buildAnnotationClassNamesByEntryConfigId,
  getTaskAnnotationClassColorOrder,
} from "@/utils/annotationClassConfig";
import {
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Flag,
  ImageOff,
  MapPin,
  Pause,
  Pencil,
  Play,
  SkipForward,
  Loader2,
  Info,
} from "lucide-react";
import {
  countPerSquareMeterToPerAcre,
  formatRoundedCountPerSquareMeterPerAcre,
  COUNT_PER_ACRE_TOOLTIP,
} from "@/lib/standCountUtils";
import { exportAnnotatedImageForEntry, downloadBlob } from "@/services/annotatedImageExportService";
import { updateEntryCollection } from "@/services/firebase";

const EMPTY_ANNOTATION_CLASS_NAMES: string[] = [];
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOrganization } from "@/hooks/useFirestore";
import type { Experiment } from "@/types/experiment";
import { getPlotMetadataEntries } from "@/utils/plotMetadataUtils";
import { db, storage } from "@/lib/firebase";
import { getDownloadURL, ref } from "firebase/storage";
import { deleteField, doc, Timestamp } from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { PlotNavigationResult } from "@/hooks/usePlotNavigation";
import { useAnnotationSequencePreloader } from "@/hooks/useAnnotationSequencePreloader";
import { useImagePreloader } from "@/hooks/useImagePreloader";
import { formatMetricValue } from "@/types/measurementMetrics";
import {
  getCachedAnnotatedThumbnail,
  setCachedAnnotatedThumbnail,
  invalidateCachedAnnotatedThumbnail,
  THUMBNAIL_DISPLAY_OPTIONS,
} from "@/services/annotatedThumbnailCache";

/**
 * AnnotatedThumbnail - displays an annotated version of an image as a thumbnail.
 * Uses global cache from annotatedThumbnailCache service to avoid re-rendering.
 */
interface AnnotatedThumbnailProps {
  imageUrl: string;
  metadataPath: string;
  alt: string;
  className?: string;
  onLoad?: () => void;
  uploadStatus?: string;
  updatedAt?: import("firebase/firestore").Timestamp;
  annotationClassNames?: string[];
  annotationClassColorOrder?: string[];
}

const AnnotatedThumbnail: React.FC<AnnotatedThumbnailProps> = ({
  imageUrl,
  metadataPath,
  alt,
  className,
  onLoad,
  uploadStatus,
  updatedAt,
  annotationClassNames,
  annotationClassColorOrder,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cachedUrl = getCachedAnnotatedThumbnail(
    imageUrl,
    metadataPath,
    updatedAt,
    annotationClassNames,
    annotationClassColorOrder
  );
  const [shouldLoad, setShouldLoad] = useState(Boolean(cachedUrl));
  const [annotatedUrl, setAnnotatedUrl] = useState<string | null>(cachedUrl);
  const [isLoading, setIsLoading] = useState(!cachedUrl);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (shouldLoad) return;

    const element = containerRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px" }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return;

    if (cachedUrl) {
      setAnnotatedUrl(cachedUrl);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function generateAnnotated(): Promise<void> {
      try {
        setIsLoading(true);
        setError(false);
        const blob = await exportAnnotatedImageForEntry(
          imageUrl,
          metadataPath,
          {
            ...THUMBNAIL_DISPLAY_OPTIONS,
            annotationClassNames,
            annotationClassColorOrder,
          },
          updatedAt
        );

        if (!cancelled) {
          const blobUrl = URL.createObjectURL(blob);
          // Use the URL returned by cache - handles race conditions where another component cached first
          const urlToUse = setCachedAnnotatedThumbnail(
            imageUrl,
            metadataPath,
            blobUrl,
            updatedAt,
            annotationClassNames,
            annotationClassColorOrder
          );
          setAnnotatedUrl(urlToUse);
          onLoad?.();
        }
      } catch (err) {
        console.error("Failed to generate annotated thumbnail:", err);
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    generateAnnotated();

    return () => {
      cancelled = true;
    };
  }, [
    annotationClassColorOrder,
    annotationClassNames,
    cachedUrl,
    imageUrl,
    metadataPath,
    onLoad,
    shouldLoad,
    updatedAt,
  ]);

  if (!shouldLoad) {
    return (
      <div
        ref={containerRef}
        className={`${className} bg-muted/50`}
      />
    );
  }

  if (isLoading) {
    return (
      <div
        ref={containerRef}
        className={`${className} bg-muted/50 flex items-center justify-center`}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !annotatedUrl) {
    return (
      <StorageImage
        storagePath={imageUrl}
        uploadStatus={uploadStatus}
        updatedAt={updatedAt}
        alt={alt}
        className={className}
      />
    );
  }

  return (
    <img
      src={annotatedUrl}
      alt={alt}
      className={className}
    />
  );
};

/** Compact display for OCR-extracted text, used in entry cards and sample lists. Click to expand. */
const ExtractedTextBadge = ({
  text,
  userDidEdit,
  lineClamp = 3,
  className = "",
}: {
  text: string;
  userDidEdit?: boolean;
  lineClamp?: 2 | 3;
  className?: string;
}) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div
      className={cn("bg-muted/40 rounded px-2 py-1 cursor-pointer", className)}
      onClick={() => setExpanded((v) => !v)}>
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <FileText className="h-3 w-3" />
        <span>Text Extracted From Image</span>
        {userDidEdit && <span className="italic">(edited)</span>}
      </div>
      <div className={cn("text-xs text-foreground", !expanded && (lineClamp === 2 ? "line-clamp-2" : "line-clamp-3"))}>
        {text}
      </div>
    </div>
  );
};

interface PlotDetailsSheetProps {
  open: boolean;
  onClose: () => void;
  plot: Plot | null;
  entryCollections: EntryCollection[];
  trialId?: string;
  experimentId?: string;
  taskId?: string;
  selectedTraitName: string;
  selectedTraitId: string;
  allEntryConfigs: EntryConfig[];
  /** The experiment's uploaded plot map columns, owned by TaskDetails so the
   *  grid and this sheet share one experiment subscription. */
  plotMap?: Experiment["plotMap"];
  navigation?: PlotNavigationResult | null;
  viewSettingsSession: ViewSettingsSession;
  onViewSettingsSessionChange: (session: ViewSettingsSession) => void;
  objectReviewSession: ObjectReviewSession;
  onObjectReviewSessionChange: (session: ObjectReviewSession) => void;
}

type ViewerMode = "preview" | "edit";

/**
 * The image-measurement model kind behind a trait, covering both the
 * imageMeasurement and stand-count-with-image-model config shapes (mirrors the
 * iOS gate, which resolves both through `resolvedModelOptions`). Decides whether
 * containment compares classes (open-set only, GLE-79) and is the basis for the
 * annotation-type preference below.
 */
const getImageMeasurementModelKindForEntryConfig = (
  entryConfig?: EntryConfig
): ImageMeasurementModelKind | undefined => {
  if (!entryConfig) return undefined;
  if (entryConfig.type === ENTRY_CONFIG_TYPES.STAND_COUNT_MEASUREMENT) {
    return entryConfig.standCountMeasurementConfig?.imgMeasureConfig?.imageMeasurementModelOptions?.kind;
  }
  if (entryConfig.type === ENTRY_CONFIG_TYPES.IMAGE_MEASUREMENT) {
    return entryConfig.imageMeasurementConfig?.imageMeasurementModelOptions?.kind;
  }
  return undefined;
};

const getImageMeasurementDescriptorConfig = (entryConfig?: EntryConfig): EntryConfig | undefined => {
  if (!entryConfig) return undefined;
  if (entryConfig.type === ENTRY_CONFIG_TYPES.IMAGE_MEASUREMENT) return entryConfig;
  if (entryConfig.type !== ENTRY_CONFIG_TYPES.STAND_COUNT_MEASUREMENT) return undefined;

  const imgMeasureConfig = entryConfig.standCountMeasurementConfig?.imgMeasureConfig;
  if (!imgMeasureConfig) return undefined;

  return {
    ...entryConfig,
    type: ENTRY_CONFIG_TYPES.IMAGE_MEASUREMENT,
    imageMeasurementConfig: imgMeasureConfig,
  } as EntryConfig;
};

/**
 * Containment-suppression params for a trait, resolved from its opt-in config
 * (`objectDetectionDefaultOptions.containmentSuppression`). This is the same
 * policy function the deferred Cloud Function and iOS use, so the viewer can
 * never disagree with the pipeline about what was suppressed.
 *
 * Reads through `getImageMeasurementDescriptorConfig` so the stand-count shape
 * (`imgMeasureConfig`) is unwrapped in exactly one place rather than re-branched
 * here — a missed branch would silently return undefined, i.e. quietly stop
 * applying the filter instead of failing.
 *
 * Returns a fresh object, so callers MUST memoize it: the viewer's filter pass is
 * an O(n²) overlap walk keyed on this identity.
 */
const getContainmentParamsForEntryConfig = (entryConfig?: EntryConfig): ContainmentParams | undefined =>
  resolveContainmentParams(
    getImageMeasurementDescriptorConfig(entryConfig)?.imageMeasurementConfig?.objectDetectionDefaultOptions,
    getImageMeasurementModelKindForEntryConfig(entryConfig)
  );

const getAnnotationTypePreferenceForEntryConfig = (entryConfig?: EntryConfig): AnnotationType | undefined => {
  const modelKind = getImageMeasurementModelKindForEntryConfig(entryConfig);
  if (!modelKind) return undefined;

  if (entryConfig?.type === ENTRY_CONFIG_TYPES.STAND_COUNT_MEASUREMENT) {
    return isKeypointModel(modelKind) || modelKind === IMAGE_MEASUREMENT_MODEL_KINDS.NONE
      ? ANNOTATION_TYPES.KEY_POINTS
      : ANNOTATION_TYPES.SEGMENTATION;
  }

  if (entryConfig?.type === ENTRY_CONFIG_TYPES.IMAGE_MEASUREMENT) {
    if (modelKind === IMAGE_MEASUREMENT_MODEL_KINDS.NONE) return undefined;
    return isKeypointModel(modelKind) ? ANNOTATION_TYPES.KEY_POINTS : ANNOTATION_TYPES.SEGMENTATION;
  }

  return undefined;
};

type ImageViewerSession =
  | {
      kind: "standalone";
      image: EntryImage;
    }
  | {
      kind: "sequence";
      selectedKey: string;
      viewerMode: ViewerMode;
    }
  | null;

type SequenceImageNavigationTarget = {
  plotId: string;
  itemKey: string;
  direction: SequenceDirection;
  viewerMode: ViewerMode;
};

type UnsavedChangesDialogState =
  | {
      kind: "discard-close";
    }
  | {
      kind: "save-failed";
      navigationTarget: SequenceImageNavigationTarget;
    }
  | null;

type SequenceNavigationSaveOverlayState = {
  title: string;
  subtitle?: string;
} | null;

type LocalPlotEntryCollectionsState = {
  plotStateKey: string;
  entryCollections: EntryCollection[];
} | null;

const MIN_SEQUENCE_NAVIGATION_SAVE_OVERLAY_VISIBLE_MS = 600;

const PlotDetailsSheet: React.FC<PlotDetailsSheetProps> = ({
  open,
  onClose,
  plot,
  entryCollections,
  trialId,
  experimentId,
  taskId,
  selectedTraitName,
  selectedTraitId,
  allEntryConfigs,
  plotMap,
  navigation,
  viewSettingsSession,
  onViewSettingsSessionChange,
  objectReviewSession,
  onObjectReviewSessionChange,
}) => {
  const { users } = useOrganization();
  // The plot map columns the user uploaded. This sheet lists them in full - it is
  // where the heatmap tooltip's "+N more" sends anyone with a wide plot map.
  const plotMetadataEntries = plot ? getPlotMetadataEntries(plot, plotMap) : [];
  const viewerRef = useRef<AnnotatedImageViewerHandle | null>(null);
  // Latest on-screen view settings the viewer reported; drives the persist prompt
  // and the snapshot carried forward while "persist" is active.
  const latestViewSettingsRef = useRef<AnnotationViewSettings | null>(null);
  // Stable identity (ref write only) so the viewer's onViewSettingsChange prop never
  // changes and the viewer's own report effects don't re-fire on parent re-renders.
  const handleViewerViewSettingsChange = useCallback((settings: AnnotationViewSettings) => {
    latestViewSettingsRef.current = settings;
  }, []);
  // Non-null while the persist prompt is open, holding the navigation the reviewer
  // triggered so we can resume it once they choose keep/reset.
  const [pendingPersistDirection, setPendingPersistDirection] = useState<SequenceDirection | null>(null);
  // Which end of the next image's object list review mode should open on. State, not a ref: it has
  // to be a prop at the moment the per-image viewer key remounts the viewer.
  const [reviewLandingEdge, setReviewLandingEdge] = useState<ReviewLandingEdge>("first");
  // Non-null while the "you've reviewed everything here" prompt is open. Its scope and plot are
  // frozen at open time so the copy can't shift under the reviewer.
  const [endOfObjectsPrompt, setEndOfObjectsPrompt] = useState<{
    scope: "image" | "plot";
    plotId?: string;
    dontAskAgain: boolean;
  } | null>(null);
  const [viewerSession, setViewerSession] = useState<ImageViewerSession>(null);
  const [viewerHasUnsavedChanges, setViewerHasUnsavedChanges] = useState(false);
  const [unsavedChangesDialogState, setUnsavedChangesDialogState] = useState<UnsavedChangesDialogState>(null);
  const [sequenceNavigationSaveOverlay, setSequenceNavigationSaveOverlay] =
    useState<SequenceNavigationSaveOverlayState>(null);
  const [pendingCrossPlotImageNavigation, setPendingCrossPlotImageNavigation] =
    useState<SequenceImageNavigationTarget | null>(null);
  const unsavedChangesDialogTrigger = useRef<"dialog-close" | "back-button">("dialog-close");
  const isViewerOpen = viewerSession !== null;
  const aggregatePersistenceNeedsRetry =
    unsavedChangesDialogState !== null && viewerRef.current?.canDiscardUnsavedChanges() === false;

  // Reviewers pan a 200-400% zoomed image continuously, and a two-finger horizontal swipe is
  // browser Back. The canvas preventDefaults its own wheel events, but the Display panel, the
  // toolbar and the letterboxed margins do not — so the swipe popped history and the guard below
  // raised "Discard unsaved changes?" mid-review, repeatedly. Containing horizontal overscroll for
  // as long as the viewer is open makes the gesture pan instead of navigate; a deliberate Back
  // press still reaches the guard. Scoped to the viewer so the rest of the app keeps swipe-back.
  // Keyed on the boolean, not the session object: that object is rebuilt for every image, which
  // would tear the class off and back on through the whole sweep.
  useEffect(() => {
    if (!isViewerOpen) return;
    document.body.classList.add("annotation-viewer-open");
    return () => document.body.classList.remove("annotation-viewer-open");
  }, [isViewerOpen]);

  // Park a dummy history entry while there are unsaved changes so the back
  // button can be intercepted. On popstate, re-push to stay and show the
  // custom discard dialog.
  //
  // Park at most one sentinel, by asking the history stack rather than remembering. Re-parking on
  // every clean→dirty flip stacked up an entry per edited image, and a reviewer working a few
  // hundred images left Back unable to escape the page in one press (every entry carries the same
  // URL, so popping one looks like nothing happened). Remembering "already parked" in a ref is the
  // trap: this sheet outlives the lightbox, so once anything consumed the sentinel — a
  // Back-triggered discard, or one dead Back press after a save — the guard never re-armed and the
  // next Back walked off the page with the edits. Reading the live state instead self-heals.
  useEffect(() => {
    if (!viewerHasUnsavedChanges) return;
    if (window.history.state?.unsavedChangesGuard !== true) {
      window.history.pushState({ unsavedChangesGuard: true }, "");
    }
    const handler = () => {
      // The pop consumed the sentinel; re-park it so the reviewer stays put.
      window.history.pushState({ unsavedChangesGuard: true }, "");
      unsavedChangesDialogTrigger.current = "back-button";
      setUnsavedChangesDialogState({ kind: "discard-close" });
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [viewerHasUnsavedChanges]);

  const { selectedOrganization, user, userRole, isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const closeImageViewer = useCallback(() => {
    // Flush the latest on-screen settings into the page session before closing so a
    // tweak-then-close (without navigating) isn't lost — reopening must restore what the
    // reviewer last saw, not the snapshot from the previous navigation. Mirrors the
    // navigate-time sync; the reference-equality guard makes it a no-op when unchanged.
    if (viewSettingsSession.choice === "persist" && viewSettingsSession.settings !== latestViewSettingsRef.current) {
      onViewSettingsSessionChange({ choice: "persist", settings: latestViewSettingsRef.current });
    }
    setSequenceNavigationSaveOverlay(null);
    setViewerHasUnsavedChanges(false);
    setUnsavedChangesDialogState(null);
    setPendingCrossPlotImageNavigation(null);
    setViewerSession(null);
  }, [onViewSettingsSessionChange, viewSettingsSession.choice, viewSettingsSession.settings]);
  const currentPlotStateKey = plot?.id ?? plot?.plotID ?? null;
  // Keep optimistic saved edits scoped to the plot they belong to so cross-plot
  // navigation never renders the next plot from the previous plot's local state.
  const [localPlotEntryCollectionsState, setLocalPlotEntryCollectionsState] =
    useState<LocalPlotEntryCollectionsState>(null);
  const displayCollections =
    currentPlotStateKey && localPlotEntryCollectionsState?.plotStateKey === currentPlotStateKey
      ? localPlotEntryCollectionsState.entryCollections
      : entryCollections;

  const updateCurrentPlotEntryCollectionsLocally = useCallback(
    (updateEntryCollections: (currentEntryCollections: EntryCollection[]) => EntryCollection[]) => {
      if (!currentPlotStateKey) return;

      setLocalPlotEntryCollectionsState((currentState) => {
        const currentEntryCollectionsForPlot =
          currentState?.plotStateKey === currentPlotStateKey ? currentState.entryCollections : entryCollections;

        return {
          plotStateKey: currentPlotStateKey,
          entryCollections: updateEntryCollections(currentEntryCollectionsForPlot),
        };
      });
    },
    [currentPlotStateKey, entryCollections]
  );

  // Tracks a version counter per metadataPath. Incrementing causes the matching
  // AnnotatedThumbnail to remount (via its key prop), bypassing the in-memory cache
  // and re-rendering the thumbnail from the updated metadata.json in Storage.
  const [thumbnailVersions, setThumbnailVersions] = useState<Record<string, number>>({});
  const [transcriptionPlayingKey, setTranscriptionPlayingKey] = useState<string | null>(null);
  const [transcriptionLoadingKey, setTranscriptionLoadingKey] = useState<string | null>(null);
  const [transcriptionErrorKey, setTranscriptionErrorKey] = useState<string | null>(null);
  const transcriptionAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const transcriptionUrlCacheRef = React.useRef<Map<string, string>>(new Map());
  const transcriptionSamples = plot ? getTranscriptionSamplesForPlot(displayCollections, plot.plotID) : [];

  // Image viewer display options state
  const [displayOptions, setDisplayOptions] = useState<AnnotationDisplayOptions>({
    ...DEFAULT_DISPLAY_OPTIONS,
    showAnnotations: true,
  });

  // Sanitize a string for use in filenames
  const sanitizeFilename = (name: string): string => {
    return name
      .replace(/[/\\?%*:|"<>]/g, "-") // Replace illegal chars with dash
      .replace(/\s+/g, "_") // Replace whitespace with underscore
      .replace(/-+/g, "-") // Collapse multiple dashes
      .replace(/_+/g, "_") // Collapse multiple underscores
      .trim();
  };

  // Download handler for thumbnail download button
  const handleThumbnailDownload = async (img: EntryImage, plotId: string) => {
    if (!img.captureMetadataCloudPath) return;

    try {
      const blob = await exportAnnotatedImageForEntry(
        img.url,
        img.captureMetadataCloudPath,
        THUMBNAIL_DISPLAY_OPTIONS,
        img.updatedAt
      );
      const traitPart = img.traitName ? `--${sanitizeFilename(img.traitName)}` : "";
      downloadBlob(blob, `${plotId}${traitPart}--sample${img.sampleKey}.jpg`);
    } catch (error) {
      console.error("Failed to download:", error);
    }
  };

  const stopTranscriptionAudio = () => {
    if (transcriptionAudioRef.current) {
      transcriptionAudioRef.current.pause();
      transcriptionAudioRef.current.currentTime = 0;
    }
    setTranscriptionPlayingKey(null);
    setTranscriptionLoadingKey(null);
  };

  const handlePlayTranscription = async (sampleKey: string, cloudAudioPath?: string) => {
    if (!cloudAudioPath) return;

    const playKey = `${sampleKey}-${cloudAudioPath}`;

    // Toggle pause if same sample is playing
    if (transcriptionPlayingKey === playKey) {
      stopTranscriptionAudio();
      return;
    }

    try {
      setTranscriptionErrorKey(null);
      setTranscriptionLoadingKey(playKey);

      let url = transcriptionUrlCacheRef.current.get(cloudAudioPath);
      if (!url) {
        const storageRef = ref(storage, cloudAudioPath);
        url = await getDownloadURL(storageRef);
        transcriptionUrlCacheRef.current.set(cloudAudioPath, url);
      }

      if (!transcriptionAudioRef.current) {
        transcriptionAudioRef.current = new Audio();
      }

      const audioEl = transcriptionAudioRef.current;
      audioEl.onended = () => {
        setTranscriptionPlayingKey(null);
        setTranscriptionLoadingKey(null);
      };
      audioEl.onerror = () => {
        setTranscriptionErrorKey(playKey);
        stopTranscriptionAudio();
      };

      audioEl.src = url;
      await audioEl.play();
      setTranscriptionPlayingKey(playKey);
    } catch (error) {
      console.error("Error playing transcription audio", error);
      setTranscriptionErrorKey(playKey);
      stopTranscriptionAudio();
    } finally {
      setTranscriptionLoadingKey(null);
    }
  };

  // Group collections by trait
  const collectionsByTrait = displayCollections.reduce(
    (acc, collection) => {
      const traitId = collection.entryConfigRef?.id;
      if (!traitId) return acc;

      if (!acc[traitId]) {
        acc[traitId] = [];
      }
      acc[traitId].push(collection);
      return acc;
    },
    {} as Record<string, EntryCollection[]>
  );

  // Filter collections to only those for the selected trait
  const selectedTraitCollections = collectionsByTrait[selectedTraitId] || [];

  // Get selected trait config and other traits
  const selectedTraitConfig = allEntryConfigs.find((config) => config.id === selectedTraitId);
  const otherTraits = allEntryConfigs.filter((config) => config.id !== selectedTraitId);

  // Calculate stats (must be before early return)
  const displayValue = getEntryDisplayValue(displayCollections, selectedTraitId);

  // Determine trait type from collection type field
  const entryType = selectedTraitCollections.find((c) => c.type)?.type || "text";
  const isNumericType = entryType === "number" || entryType === "percentage";

  // Get trait-specific sample count
  const { sampleCount: totalSamples } = getTraitStats(displayCollections, selectedTraitId);

  // Build a map from entry config ID to trait name for image filename generation
  const configIdToName = React.useMemo(() => {
    const map = new Map<string, string>();
    allEntryConfigs.forEach((config) => {
      if (config.id) {
        map.set(config.id, config.name || config.id);
      }
    });
    return map;
  }, [allEntryConfigs]);

  const configById = useMemo(() => {
    const map = new Map<string, EntryConfig>();
    allEntryConfigs.forEach((config) => {
      if (config.id) {
        map.set(config.id, config);
      }
    });
    return map;
  }, [allEntryConfigs]);
  const annotationClassNamesByEntryConfigId = useMemo(
    () => buildAnnotationClassNamesByEntryConfigId(allEntryConfigs),
    [allEntryConfigs]
  );

  const allImages = displayCollections.flatMap((collection) => {
    const configId = collection.entryConfigRef?.id;
    const traitName = configId ? configIdToName.get(configId) : undefined;
    return extractImagesFromEntryCollection(collection, traitName);
  });

  const collectionTraitIdsByCollectionId = useMemo(() => {
    const map = new Map<string, string | undefined>();
    displayCollections.forEach((collection) => {
      if (collection.id) {
        map.set(collection.id, collection.entryConfigRef?.id);
      }
    });
    return map;
  }, [displayCollections]);

  // Build user name lookup from org-scoped userPermissions
  const userNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const user of users) {
      const displayName = `${user.firstName} ${user.lastName}`.trim();
      names[user.id] = displayName || user.email || "Unknown User";
    }
    return names;
  }, [users]);

  const isImageInSelectedTrait = useCallback(
    (image: EntryImage | EditablePlotImageSequenceItem | null | undefined) => {
      if (!image) return false;
      const traitId =
        "traitId" in image && image.traitId
          ? image.traitId
          : collectionTraitIdsByCollectionId.get(image.collectionId || "");
      return traitId === selectedTraitId;
    },
    [collectionTraitIdsByCollectionId, selectedTraitId]
  );

  const canReviewImage = useCallback(
    (image: EntryImage | EditablePlotImageSequenceItem | null | undefined) => {
      if (!image) return false;
      if (isSuperAdmin || userRole === "admin" || userRole === "editor") return true;
      if (userRole !== "collector" || !user) return false;
      if ("userId" in image && image.userId) {
        return image.userId === user.uid;
      }
      if (!image.collectionId) return false;
      const collection = displayCollections.find((entryCollection) => entryCollection.id === image.collectionId);
      if (!collection?.createdBy) return false;
      const creatorPath = typeof collection.createdBy === "string" ? collection.createdBy : collection.createdBy.path;
      return creatorPath === `users/${user.uid}`;
    },
    [displayCollections, isSuperAdmin, user, userRole]
  );
  const canEditImage = useCallback(
    (image: EntryImage | EditablePlotImageSequenceItem | null | undefined) => {
      return Boolean(
        image && canEditRenderedAnnotationImage(image) && isEditableAnnotationImage(image) && canReviewImage(image)
      );
    },
    [canReviewImage]
  );

  const userEmails = useMemo(() => {
    const emails: Record<string, string> = {};
    for (const user of users) {
      emails[user.id] = user.email || "Unknown User";
    }
    return emails;
  }, [users]);

  const buildSequenceForPlot = useCallback(
    (candidatePlot: Plot, candidateCollections: EntryCollection[]) =>
      buildPlotImageSequence({
        plot: candidatePlot,
        entryCollections: candidateCollections,
        entryConfigs: allEntryConfigs,
        userEmails,
        selectedTraitId,
      }),
    [allEntryConfigs, selectedTraitId, userEmails]
  );

  const currentPlotImageSequence = useMemo(
    () => (plot ? buildSequenceForPlot(plot, displayCollections) : []),
    [buildSequenceForPlot, displayCollections, plot]
  );

  const currentSequenceImage = useMemo(() => {
    if (viewerSession?.kind !== "sequence") return null;
    return currentPlotImageSequence.find((item) => item.key === viewerSession.selectedKey) ?? null;
  }, [currentPlotImageSequence, viewerSession]);

  const currentViewerImage = viewerSession?.kind === "sequence" ? currentSequenceImage : (viewerSession?.image ?? null);
  const currentViewerImageIsUnreviewedDeferred = isDeferredAnalysisUnreviewedEntry(currentViewerImage);
  const currentViewerImageIsReviewedDeferred = isDeferredAnalysisReviewedEntry(currentViewerImage);
  const currentViewerTraitId = currentViewerImage?.collectionId
    ? collectionTraitIdsByCollectionId.get(currentViewerImage.collectionId)
    : undefined;
  const currentViewerEntryConfig = currentViewerTraitId ? configById.get(currentViewerTraitId) : undefined;
  const currentViewerAnnotationTypePreference = getAnnotationTypePreferenceForEntryConfig(currentViewerEntryConfig);
  // Memoized because the identity is a dependency of the viewer's filter memo:
  // a fresh object each render would re-run the whole O(n²) overlap walk on every
  // unrelated state change in this component. (Before GLE-79 this prop was a
  // string, so the stability came for free.)
  const currentViewerContainmentParams = useMemo(
    () => getContainmentParamsForEntryConfig(currentViewerEntryConfig),
    [currentViewerEntryConfig]
  );
  const currentViewerMeasurementDescriptors = useMemo(
    () => getDescriptorsForEntryConfig(getImageMeasurementDescriptorConfig(currentViewerEntryConfig)),
    [currentViewerEntryConfig]
  );
  const taskAnnotationClassColorOrder = useMemo(
    () => getTaskAnnotationClassColorOrder(allEntryConfigs),
    [allEntryConfigs]
  );
  const currentViewerAnnotationClassNames = currentViewerTraitId
    ? (annotationClassNamesByEntryConfigId.get(currentViewerTraitId) ?? EMPTY_ANNOTATION_CLASS_NAMES)
    : EMPTY_ANNOTATION_CLASS_NAMES;
  const isViewerEditMode = viewerSession?.kind === "sequence" && viewerSession.viewerMode === "edit";
  const viewerCanEdit = canEditImage(currentViewerImage) && isImageInSelectedTrait(currentViewerImage);
  const viewerCanReview = canReviewImage(currentViewerImage) && isImageInSelectedTrait(currentViewerImage);
  const currentViewerImageIsEditableForSelectedTrait =
    !!currentViewerImage &&
    canEditRenderedAnnotationImage(currentViewerImage) &&
    isEditableAnnotationImage(currentViewerImage) &&
    isImageInSelectedTrait(currentViewerImage);
  const currentViewerMetadataPath = shouldRenderAnnotatedImage(currentViewerImage)
    ? currentViewerImage?.captureMetadataCloudPath
    : undefined;
  const viewerEntryPath =
    selectedOrganization && trialId && experimentId && taskId && currentViewerImage?.collectionId
      ? `organizations/${selectedOrganization.id}/trials/${trialId}/experiments/${experimentId}/tasks/${taskId}/entryCollections/${currentViewerImage.collectionId}`
      : undefined;

  const allNavigableImages = useMemo(() => {
    if (!plot) return [];
    const orderedPlots = navigation?.orderedPlots ?? [plot];
    return orderEditableReviewQueue(orderedPlots.flatMap((candidatePlot) =>
      candidatePlot.plotID === plot.plotID
        ? currentPlotImageSequence
        : buildSequenceForPlot(candidatePlot, navigation?.getEntriesForPlot(candidatePlot) ?? [])
    ));
  }, [buildSequenceForPlot, currentPlotImageSequence, navigation, plot]);

  const getSequenceNavigationTarget = useCallback(
    (direction: SequenceDirection): SequenceImageNavigationTarget | null => {
      if (!plot || viewerSession?.kind !== "sequence") return null;

      const target = resolveEditableImageNavigationTarget({
        currentPlotId: plot.plotID,
        currentItemKey: viewerSession.selectedKey,
        direction,
        queue: allNavigableImages,
      });

      if (!target) return null;

      const targetItem = allNavigableImages.find((item) => item.key === target.itemKey);
      const targetViewerMode =
        viewerSession.viewerMode === "edit" && canEditImage(targetItem) ? viewerSession.viewerMode : "preview";

      return {
        ...target,
        direction,
        viewerMode: targetViewerMode,
      };
    },
    [allNavigableImages, canEditImage, plot, viewerSession]
  );

  const previousSequenceNavigationTarget = useMemo(
    () => getSequenceNavigationTarget("prev"),
    [getSequenceNavigationTarget]
  );
  const nextSequenceNavigationTarget = useMemo(
    () => getSequenceNavigationTarget("next"),
    [getSequenceNavigationTarget]
  );

  const getSequenceImageForNavigationTarget = useCallback(
    (navigationTarget: SequenceImageNavigationTarget | null): EditablePlotImageSequenceItem | null => {
      if (!navigationTarget) return null;
      return allNavigableImages.find((item) => item.key === navigationTarget.itemKey) ?? null;
    },
    [allNavigableImages]
  );

  const annotationSequencePreloadTargets = useMemo(
    () =>
      [previousSequenceNavigationTarget, nextSequenceNavigationTarget]
        .map(getSequenceImageForNavigationTarget)
        .filter((image): image is EditablePlotImageSequenceItem => Boolean(image))
        .map((image) => ({
          imageUrl: image.url,
          metadataPath: shouldRenderAnnotatedImage(image) ? image.captureMetadataCloudPath : undefined,
          uploadStatus: image.uploadStatus,
          updatedAt: image.updatedAt,
        })),
    [getSequenceImageForNavigationTarget, nextSequenceNavigationTarget, previousSequenceNavigationTarget]
  );

  const openImageViewer = useCallback(
    (image: EntryImage) => {
      // A stale "last" from an earlier backward sweep must never leak into a freshly opened viewer.
      setReviewLandingEdge("first");
      if (plot && isSequenceImage(image) && isImageInSelectedTrait(image)) {
        setViewerSession({
          kind: "sequence",
          selectedKey: getEditablePlotImageKey(plot.plotID, image),
          viewerMode: "preview",
        });
        return;
      }

      setViewerSession({
        kind: "standalone",
        image,
      });
    },
    [isImageInSelectedTrait, plot]
  );

  const openImageEditor = useCallback(
    (image: EntryImage) => {
      if (!plot || !isEditableAnnotationImage(image) || !isImageInSelectedTrait(image) || !canEditImage(image)) {
        return;
      }
      setReviewLandingEdge("first");

      setViewerSession({
        kind: "sequence",
        selectedKey: getEditablePlotImageKey(plot.plotID, image),
        viewerMode: "edit",
      });
    },
    [canEditImage, isImageInSelectedTrait, plot]
  );

  const navigateToSequenceImage = useCallback(
    (navigationTarget: SequenceImageNavigationTarget) => {
      // While persisting, carry the latest on-screen tweaks forward to the next image.
      // Lives here (not in performSequenceNavigation) so every navigation path carries
      // them — including the save-failed "Discard & continue" retry. Snapshots are
      // fresh objects only when something actually changed, so reference equality
      // skips the state write (and its re-render) when nothing moved.
      if (viewSettingsSession.choice === "persist" && viewSettingsSession.settings !== latestViewSettingsRef.current) {
        onViewSettingsSessionChange({ choice: "persist", settings: latestViewSettingsRef.current });
      }

      if (navigationTarget.plotId === plot?.plotID) {
        setViewerHasUnsavedChanges(false);
        setViewerSession((currentSession) =>
          currentSession?.kind === "sequence"
            ? {
                ...currentSession,
                selectedKey: navigationTarget.itemKey,
                viewerMode: navigationTarget.viewerMode,
              }
            : currentSession
        );
        return;
      }

      const targetPlot = navigation?.orderedPlots.find((orderedPlot) => orderedPlot.plotID === navigationTarget.plotId);
      if (!targetPlot || !navigation) return;

      setViewerHasUnsavedChanges(false);
      setPendingCrossPlotImageNavigation(navigationTarget);
      navigation.goToPlot(targetPlot);
    },
    [navigation, onViewSettingsSessionChange, plot?.plotID, viewSettingsSession.choice, viewSettingsSession.settings]
  );

  const performSequenceNavigation = useCallback(
    async (direction: SequenceDirection) => {
      if (sequenceNavigationSaveOverlay) return;

      const navigationTarget = direction === "prev" ? previousSequenceNavigationTarget : nextSequenceNavigationTarget;
      if (!navigationTarget) return;

      const shouldSaveBeforeNavigation =
        viewerHasUnsavedChanges || (isViewerEditMode && currentViewerImageIsUnreviewedDeferred);

      if (shouldSaveBeforeNavigation) {
        if (!viewerRef.current) return;
        const confirmed = await viewerRef.current.confirmOutsideBoundaryAnnotationsBeforeSave();
        if (!confirmed) return;

        const targetScope = navigationTarget.plotId === plot?.plotID ? "image" : "plot";
        const targetDirection = navigationTarget.direction === "next" ? "next" : "previous";
        const overlayShownAt = Date.now();
        setSequenceNavigationSaveOverlay({
          title: `Saving annotations and advancing to the ${targetDirection} ${targetScope}...`,
          subtitle: "Please wait...",
        });

        try {
          const saveStatus = await viewerRef.current.saveIfThereAreUnsavedChanges();
          const remainingOverlayDuration =
            MIN_SEQUENCE_NAVIGATION_SAVE_OVERLAY_VISIBLE_MS - (Date.now() - overlayShownAt);
          if (remainingOverlayDuration > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, remainingOverlayDuration));
          }
          if (saveStatus === "error") {
            setUnsavedChangesDialogState({
              kind: "save-failed",
              navigationTarget,
            });
            return;
          }
          if (saveStatus === "new_changes") {
            return;
          }
        } finally {
          setSequenceNavigationSaveOverlay(null);
        }
      }

      navigateToSequenceImage(navigationTarget);
    },
    [
      currentViewerImageIsUnreviewedDeferred,
      isViewerEditMode,
      navigateToSequenceImage,
      nextSequenceNavigationTarget,
      plot?.plotID,
      previousSequenceNavigationTarget,
      sequenceNavigationSaveOverlay,
      viewerHasUnsavedChanges,
    ]
  );

  // Public navigation entry. The first time the reviewer navigates after changing a
  // tracked view setting, ask whether to keep it; otherwise navigate straight away.
  //
  // `landingEdge` says which end of the NEXT image's object list review mode should open on. Every
  // caller declares it here rather than setting the state itself, so a path that doesn't care (the
  // chevrons, the end-of-objects prompt) can't silently inherit a stale "last" left behind by an
  // earlier backward keyboard sweep and skip that image's objects entirely.
  const goToAdjacentSequenceImage = useCallback(
    async (direction: SequenceDirection, landingEdge: ReviewLandingEdge = "first") => {
      // Bail before the persist prompt when there is no adjacent image — otherwise the
      // reviewer answers a dialog on the first/last image, consuming the once-per-session
      // choice, yet nothing navigates.
      const navigationTarget = direction === "prev" ? previousSequenceNavigationTarget : nextSequenceNavigationTarget;
      if (!navigationTarget) return;
      setReviewLandingEdge(landingEdge);
      if (shouldAskToPersistViewSettings(viewSettingsSession.choice, latestViewSettingsRef.current)) {
        setPendingPersistDirection(direction);
        return;
      }
      await performSequenceNavigation(direction);
    },
    [
      nextSequenceNavigationTarget,
      performSequenceNavigation,
      previousSequenceNavigationTarget,
      viewSettingsSession.choice,
    ]
  );

  /**
   * Arrow-key entry point while the annotation viewer is open. The editor gets first refusal: in
   * review-objects mode a left/right arrow steps the object cursor instead of changing image, and
   * only running off the end of the list falls through to navigation.
   */
  const handleSequenceArrow = useCallback(
    (direction: SequenceDirection, source: "arrow" | "enter" = "arrow") => {
      const step = viewerRef.current?.stepReviewObject(direction) ?? "inactive";
      const target = direction === "prev" ? previousSequenceNavigationTarget : nextSequenceNavigationTarget;
      const outcome = resolveReviewArrowOutcome({
        direction,
        step,
        hasNavigationTarget: Boolean(target),
        suppressEndOfObjectsPrompt: objectReviewSession.suppressEndOfObjectsPrompt,
        source,
      });

      switch (outcome.kind) {
        case "consumed":
        case "ignore":
          return;
        case "navigate":
          void goToAdjacentSequenceImage(outcome.direction, outcome.landingEdge);
          return;
        case "prompt":
          setEndOfObjectsPrompt({
            // "prompt" is only returned when a target exists, so this never defaults to "plot".
            scope: target?.plotId === plot?.plotID ? "image" : "plot",
            plotId: plot?.plotID,
            dontAskAgain: false,
          });
      }
    },
    [
      goToAdjacentSequenceImage,
      nextSequenceNavigationTarget,
      objectReviewSession.suppressEndOfObjectsPrompt,
      plot?.plotID,
      previousSequenceNavigationTarget,
    ]
  );

  const confirmEndOfObjects = useCallback(() => {
    // "Don't ask again" commits only on confirm — cancelling discards it along with the navigation.
    if (endOfObjectsPrompt?.dontAskAgain) onObjectReviewSessionChange({ suppressEndOfObjectsPrompt: true });
    setEndOfObjectsPrompt(null);
    void goToAdjacentSequenceImage("next");
  }, [endOfObjectsPrompt?.dontAskAgain, goToAdjacentSequenceImage, onObjectReviewSessionChange]);

  // Resume the pending navigation once the reviewer answers the persist prompt.
  const resolvePersistChoice = useCallback(
    (choice: "persist" | "reset") => {
      const direction = pendingPersistDirection;
      setPendingPersistDirection(null);
      onViewSettingsSessionChange(
        choice === "persist"
          ? { choice: "persist", settings: latestViewSettingsRef.current }
          : { choice: "reset", settings: null }
      );
      if (direction) void performSequenceNavigation(direction);
    },
    [onViewSettingsSessionChange, pendingPersistDirection, performSequenceNavigation]
  );

  useEffect(() => {
    if (!pendingCrossPlotImageNavigation || !plot || plot.plotID !== pendingCrossPlotImageNavigation.plotId) {
      return;
    }

    if (currentPlotImageSequence.length === 0) {
      return;
    }

    const targetKeyExists = currentPlotImageSequence.some(
      (item) => item.key === pendingCrossPlotImageNavigation.itemKey
    );

    setViewerSession({
      kind: "sequence",
      selectedKey: targetKeyExists ? pendingCrossPlotImageNavigation.itemKey : currentPlotImageSequence[0].key,
      viewerMode: pendingCrossPlotImageNavigation.viewerMode,
    });
    setPendingCrossPlotImageNavigation(null);
  }, [currentPlotImageSequence, pendingCrossPlotImageNavigation, plot]);

  useEffect(() => {
    if (viewerSession?.kind !== "sequence" || pendingCrossPlotImageNavigation) return;
    if (currentPlotImageSequence.length === 0) {
      closeImageViewer();
      return;
    }

    const hasSelectedItem = currentPlotImageSequence.some((item) => item.key === viewerSession.selectedKey);
    if (!hasSelectedItem) {
      setViewerSession({
        kind: "sequence",
        selectedKey: currentPlotImageSequence[0].key,
        viewerMode: viewerSession.viewerMode,
      });
    }
  }, [closeImageViewer, currentPlotImageSequence, pendingCrossPlotImageNavigation, viewerSession]);

  // Cleanup transcription audio when sheet closes
  useEffect(() => {
    if (!open) return;
    return () => {
      if (transcriptionAudioRef.current) {
        transcriptionAudioRef.current.pause();
        transcriptionAudioRef.current.currentTime = 0;
      }
      setTranscriptionPlayingKey(null);
    };
  }, [open]);

  useEffect(() => {
    setLocalPlotEntryCollectionsState((currentState) => {
      if (!currentPlotStateKey) return null;
      return currentState?.plotStateKey === currentPlotStateKey ? currentState : null;
    });
  }, [currentPlotStateKey]);

  const prevPlot = navigation?.prevPlot ?? null;
  const nextPlot = navigation?.nextPlot ?? null;
  const upPlot = navigation?.upPlot ?? null;
  const downPlot = navigation?.downPlot ?? null;

  // Memoize adjacent plots array to prevent unnecessary preloader re-runs
  const adjacentPlots = React.useMemo(() => {
    if (!prevPlot && !nextPlot && !upPlot && !downPlot) return [];
    return [prevPlot, nextPlot, upPlot, downPlot];
  }, [prevPlot, nextPlot, upPlot, downPlot]);

  // Preload images for adjacent plots to improve navigation performance
  useImagePreloader({
    adjacentPlots,
    getEntriesForPlot: navigation?.getEntriesForPlot ?? (() => []),
    entryConfigs: allEntryConfigs,
    enabled: open && !viewerSession, // Only preload when sheet is open and lightbox is closed
  });

  useAnnotationSequencePreloader({
    targets: annotationSequencePreloadTargets,
    enabled: open && viewerSession?.kind === "sequence" && !!currentViewerImage,
  });

  // Keyboard navigation
  useEffect(() => {
    if (!open || !navigation) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (sequenceNavigationSaveOverlay || pendingPersistDirection || endOfObjectsPrompt) {
        return;
      }

      // Don't navigate while the user is typing or adjusting a slider by keyboard
      if (shouldIgnoreSequenceNavigationKeys(e.target)) {
        return;
      }

      switch (e.key) {
        case "ArrowLeft":
          if (viewerSession?.kind === "sequence") {
            e.preventDefault();
            handleSequenceArrow("prev");
          } else if (viewerSession) {
            return;
          } else if (navigation.canGoPrev) {
            e.preventDefault();
            navigation.goToPrev();
          }
          break;
        case "ArrowRight":
          if (viewerSession?.kind === "sequence") {
            e.preventDefault();
            handleSequenceArrow("next");
          } else if (viewerSession) {
            return;
          } else if (navigation.canGoNext) {
            e.preventDefault();
            navigation.goToNext();
          }
          break;
        case "Enter":
          // Enter is a second "next object" key so a reviewer can sweep an image one-handed, and
          // it carries through to the end-of-objects prompt (where Enter then confirms). Skip it
          // when a control has focus — Enter already activates buttons and links natively, and
          // hijacking it would fire both.
          if (viewerSession?.kind !== "sequence") return;
          if ((e.target as HTMLElement | null)?.closest("button, a, [role='button'], [role='checkbox']")) return;
          e.preventDefault();
          handleSequenceArrow("next", "enter");
          break;
        case "ArrowUp":
          if (viewerSession) return;
          if (navigation.canGoUp) {
            e.preventDefault();
            navigation.goToUp();
          }
          break;
        case "ArrowDown":
          if (viewerSession) return;
          if (navigation.canGoDown) {
            e.preventDefault();
            navigation.goToDown();
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    endOfObjectsPrompt,
    handleSequenceArrow,
    navigation,
    open,
    pendingPersistDirection,
    sequenceNavigationSaveOverlay,
    viewerSession,
  ]);

  const currentSequenceImageIndex = currentSequenceImage
    ? allNavigableImages.findIndex((image) => image.key === currentSequenceImage.key)
    : -1;
  const { rankedReviewImages, firstUnreviewedImage, rankedReviewedCount } = useMemo(() => {
    const rankedReviewImages = allNavigableImages.filter((image) => isReviewPriority(image.reviewPriority));
    return {
      rankedReviewImages,
      firstUnreviewedImage: rankedReviewImages.find(isDeferredAnalysisUnreviewedEntry),
      rankedReviewedCount: rankedReviewImages.filter(isDeferredAnalysisReviewedEntry).length,
    };
  }, [allNavigableImages]);
  const showImageSequenceNavigation = viewerSession?.kind === "sequence";
  const isLoadingCrossPlotImageNavigation =
    pendingCrossPlotImageNavigation !== null && plot?.plotID !== pendingCrossPlotImageNavigation.plotId;
  let resumeQueueDisabledReason: string | undefined;
  if (viewerHasUnsavedChanges) {
    resumeQueueDisabledReason = "Save or discard your edits before you resume the queue";
  } else if (sequenceNavigationSaveOverlay || pendingPersistDirection || endOfObjectsPrompt || isLoadingCrossPlotImageNavigation) {
    resumeQueueDisabledReason = "Finish the current image operation before you resume the queue";
  } else if (!firstUnreviewedImage) {
    resumeQueueDisabledReason = rankedReviewedCount === rankedReviewImages.length
      ? "Every ranked image is reviewed" : "No ranked image is ready for review; check its analysis status";
  } else if (firstUnreviewedImage.key === currentSequenceImage?.key) {
    resumeQueueDisabledReason = "You are at the first unreviewed image";
  }
  // A clean edit session can resume without confirmation. Unlike the arrows, this action skips review.

  const setImageReviewStatus = useCallback(
    async (
      image: EntryImage | EditablePlotImageSequenceItem | null | undefined,
      nextReviewStatus: "reviewed" | "deferred_unreviewed"
    ) => {
      if (!image?.collectionId || !trialId || !experimentId || !taskId) {
        return;
      }

      const entryMapField = getDeferredAnalysisEntryMapFieldForImageType(image.type);

      if (!entryMapField) return;

      if (nextReviewStatus === "reviewed" && !user) {
        throw new Error("Sign in before you mark an image reviewed.");
      }

      const basePath = `${entryMapField}.${image.sampleKey}`;
      const reviewedAt = nextReviewStatus === "reviewed" ? Timestamp.now() : undefined;
      const reviewedBy = nextReviewStatus === "reviewed" && user ? doc(db, "users", user.uid) : undefined;
      const fieldUpdates: Record<string, unknown> =
        nextReviewStatus === "reviewed"
          ? {
              [`${basePath}.analysisReviewStatus`]: "reviewed",
              [`${basePath}.analysisReviewedAt`]: reviewedAt,
            }
          : {
              [`${basePath}.analysisReviewStatus`]: "deferred_unreviewed",
              [`${basePath}.analysisReviewedAt`]: deleteField(),
              [`${basePath}.analysisReviewedBy`]: deleteField(),
            };

      if (nextReviewStatus === "reviewed" && reviewedBy) {
        fieldUpdates[`${basePath}.analysisReviewedBy`] = reviewedBy;
      }

      await updateEntryCollection(
        trialId,
        experimentId,
        taskId,
        image.collectionId,
        fieldUpdates as Partial<EntryCollection>
      );

      updateCurrentPlotEntryCollectionsLocally((currentEntryCollectionsForPlot) =>
        currentEntryCollectionsForPlot.map((entryCollection) => {
          if (entryCollection.id !== image.collectionId) return entryCollection;
          const entries = entryCollection[entryMapField];
          const currentEntry = entries?.[image.sampleKey];
          if (!currentEntry) return entryCollection;

          const nextEntry = {
            ...currentEntry,
            analysisReviewStatus: nextReviewStatus,
            ...(nextReviewStatus === "reviewed" && reviewedAt ? { analysisReviewedAt: reviewedAt } : {}),
            ...(nextReviewStatus === "reviewed" && reviewedBy ? { analysisReviewedBy: reviewedBy } : {}),
          };
          if (nextReviewStatus === "deferred_unreviewed") {
            delete nextEntry.analysisReviewedAt;
            delete nextEntry.analysisReviewedBy;
          }

          return {
            ...entryCollection,
            [entryMapField]: {
              ...entries,
              [image.sampleKey]: nextEntry,
            },
          };
        })
      );

      setViewerSession((currentSession) => {
        if (
          currentSession?.kind !== "standalone" ||
          currentSession.image.collectionId !== image.collectionId ||
          currentSession.image.sampleKey !== image.sampleKey
        ) {
          return currentSession;
        }

        return {
          ...currentSession,
          image: {
            ...currentSession.image,
            analysisReviewStatus: nextReviewStatus,
          },
        };
      });
    },
    [experimentId, taskId, trialId, updateCurrentPlotEntryCollectionsLocally, user]
  );

  const markCurrentViewerImageReviewed = useCallback(async () => {
    if (!currentViewerImageIsUnreviewedDeferred) return;
    await setImageReviewStatus(currentViewerImage, "reviewed");
  }, [currentViewerImage, currentViewerImageIsUnreviewedDeferred, setImageReviewStatus]);

  const markCurrentViewerImageUnreviewed = useCallback(async () => {
    if (!currentViewerImageIsReviewedDeferred) return;
    await setImageReviewStatus(currentViewerImage, "deferred_unreviewed");
  }, [currentViewerImage, currentViewerImageIsReviewedDeferred, setImageReviewStatus]);

  const buildCurrentViewerReviewedUpdates = useCallback((): Record<string, unknown> => {
    if (currentViewerImage?.analysisReviewStatus !== "deferred_unreviewed") return {};
    if (!user) throw new Error("Sign in before you mark an image reviewed.");
    return {
      analysisReviewStatus: "reviewed",
      analysisReviewedAt: Timestamp.now(),
      analysisReviewedBy: doc(db, "users", user.uid),
    };
  }, [currentViewerImage?.analysisReviewStatus, user]);

  const handleViewerSave = useCallback(
    async (metrics: {
      countPerSquareMeter?: number;
      countPerAcre?: number;
      objectCount?: number;
      avgLengthMeters?: number | null;
      avgWidthMeters?: number | null;
      totalWeightGrams?: number | null;
      additionalMeasurements?: AdditionalMeasurementAvgMetrics;
      userDidOverride?: boolean;
    }) => {
      if (!currentViewerImage?.captureMetadataCloudPath) return;

      invalidateCachedAnnotatedThumbnail(
        currentViewerImage.url,
        currentViewerImage.captureMetadataCloudPath,
        currentViewerImage.updatedAt,
        currentViewerAnnotationClassNames,
        taskAnnotationClassColorOrder
      );
      invalidateAnnotationMetadataCache(currentViewerImage.captureMetadataCloudPath);

      setThumbnailVersions((previousVersions) => ({
        ...previousVersions,
        [currentViewerImage.captureMetadataCloudPath]:
          (previousVersions[currentViewerImage.captureMetadataCloudPath] ?? 0) + 1,
      }));

      if (
        currentViewerImage.type === ENTRY_CONFIG_TYPES.STAND_COUNT_MEASUREMENT &&
        currentViewerImage.collectionId &&
        trialId &&
        experimentId &&
        taskId
      ) {
        const collection = displayCollections.find(
          (entryCollection) => entryCollection.id === currentViewerImage.collectionId
        );
        if (collection?.standCountMeasurementEntries) {
          const existingEntry = collection.standCountMeasurementEntries[currentViewerImage.sampleKey];
          const reviewUpdates = buildCurrentViewerReviewedUpdates();
          const entryUpdates = { ...existingEntry };
          const persistedUpdates: Record<string, unknown> = {};

          const sampleAreaSquareMeters =
            existingEntry?.standCountBoxLengthMeters != null &&
            existingEntry?.standCountBoxWidthMeters != null &&
            existingEntry.standCountBoxLengthMeters > 0 &&
            existingEntry.standCountBoxWidthMeters > 0
              ? existingEntry.standCountBoxLengthMeters * existingEntry.standCountBoxWidthMeters
              : undefined;
          const countPerSquareMeter =
            metrics.countPerSquareMeter ??
            (metrics.objectCount !== undefined && sampleAreaSquareMeters
              ? Math.round((metrics.objectCount / sampleAreaSquareMeters) * 100) / 100
              : undefined);

          if (countPerSquareMeter !== undefined) {
            entryUpdates.countPerSquareMeter = countPerSquareMeter;
            entryUpdates.countPerAcre = metrics.countPerAcre ?? countPerSquareMeterToPerAcre(countPerSquareMeter);
            persistedUpdates.countPerSquareMeter = entryUpdates.countPerSquareMeter;
            persistedUpdates.countPerAcre = entryUpdates.countPerAcre;
          }

          const hasSegmentationMetrics =
            metrics.objectCount !== undefined ||
            metrics.avgLengthMeters !== undefined ||
            metrics.avgWidthMeters !== undefined ||
            metrics.totalWeightGrams !== undefined ||
            metrics.additionalMeasurements !== undefined;

          if (hasSegmentationMetrics) {
            const imageMeasurementUpdates: Record<string, number | boolean> = {};
            if (metrics.objectCount !== undefined) {
              imageMeasurementUpdates.objectCount = metrics.objectCount;
              persistedUpdates["imageMeasurement.objectCount"] = metrics.objectCount;
            }
            if (metrics.avgLengthMeters !== undefined && metrics.avgLengthMeters !== null) {
              imageMeasurementUpdates.avgLengthMeters = metrics.avgLengthMeters;
              persistedUpdates["imageMeasurement.avgLengthMeters"] = metrics.avgLengthMeters;
            }
            if (metrics.avgWidthMeters !== undefined && metrics.avgWidthMeters !== null) {
              imageMeasurementUpdates.avgWidthMeters = metrics.avgWidthMeters;
              persistedUpdates["imageMeasurement.avgWidthMeters"] = metrics.avgWidthMeters;
            }
            if (metrics.totalWeightGrams !== undefined && metrics.totalWeightGrams !== null) {
              imageMeasurementUpdates.totalWeightGrams = metrics.totalWeightGrams;
              persistedUpdates["imageMeasurement.totalWeightGrams"] = metrics.totalWeightGrams;
            }
            if (metrics.userDidOverride) {
              imageMeasurementUpdates.userDidOverride = true;
              persistedUpdates["imageMeasurement.userDidOverride"] = true;
            }
            entryUpdates.imageMeasurement = { ...existingEntry?.imageMeasurement, ...imageMeasurementUpdates };
            if (metrics.additionalMeasurements !== undefined) {
              const nextAdditionalMeasurements = applyAdditionalMeasurementAvgMetrics(
                existingEntry?.imageMeasurement?.additionalMeasurements,
                metrics.additionalMeasurements
              );
              if (nextAdditionalMeasurements) {
                entryUpdates.imageMeasurement.additionalMeasurements = nextAdditionalMeasurements;
              } else {
                delete entryUpdates.imageMeasurement.additionalMeasurements;
              }
              Object.assign(
                persistedUpdates,
                buildAdditionalMeasurementAvgFieldUpdates(
                  metrics.additionalMeasurements,
                  deleteField(),
                  "imageMeasurement.additionalMeasurements"
                )
              );
            }
            if (metrics.avgLengthMeters === null) {
              delete entryUpdates.imageMeasurement.avgLengthMeters;
              persistedUpdates["imageMeasurement.avgLengthMeters"] = deleteField();
            }
            if (metrics.avgWidthMeters === null) {
              delete entryUpdates.imageMeasurement.avgWidthMeters;
              persistedUpdates["imageMeasurement.avgWidthMeters"] = deleteField();
            }
            if (metrics.totalWeightGrams === null) {
              delete entryUpdates.imageMeasurement.totalWeightGrams;
              persistedUpdates["imageMeasurement.totalWeightGrams"] = deleteField();
            }
          }
          Object.assign(entryUpdates, reviewUpdates);
          Object.assign(persistedUpdates, reviewUpdates);
          if (existingEntry?.analysisStatus === "failed") {
            delete entryUpdates.analysisStatus;
            delete entryUpdates.analysisError;
            persistedUpdates.analysisStatus = deleteField();
            persistedUpdates.analysisError = deleteField();
          }

          await updateEntryCollection(
            trialId,
            experimentId,
            taskId,
            currentViewerImage.collectionId,
            buildNestedSampleFieldUpdates(
              "standCountMeasurementEntries",
              currentViewerImage.sampleKey,
              persistedUpdates
            ) as Partial<EntryCollection>
          );

          updateCurrentPlotEntryCollectionsLocally((currentEntryCollectionsForPlot) =>
            currentEntryCollectionsForPlot.map((entryCollection) => {
              if (entryCollection.id !== currentViewerImage.collectionId) return entryCollection;
              const currentEntry = entryCollection.standCountMeasurementEntries?.[currentViewerImage.sampleKey];
              if (!currentEntry) return entryCollection;

              return {
                ...entryCollection,
                standCountMeasurementEntries: {
                  ...entryCollection.standCountMeasurementEntries,
                  [currentViewerImage.sampleKey]: entryUpdates,
                },
              };
            })
          );
        }
      }

      if (
        (metrics.objectCount !== undefined ||
          metrics.avgLengthMeters !== undefined ||
          metrics.avgWidthMeters !== undefined ||
          metrics.totalWeightGrams !== undefined ||
          metrics.additionalMeasurements !== undefined) &&
        currentViewerImage.type === ENTRY_CONFIG_TYPES.IMAGE_MEASUREMENT &&
        currentViewerImage.collectionId &&
        trialId &&
        experimentId &&
        taskId
      ) {
        const collection = displayCollections.find(
          (entryCollection) => entryCollection.id === currentViewerImage.collectionId
        );
        if (collection?.imageMeasurementEntries) {
          const existingEntry = collection.imageMeasurementEntries[currentViewerImage.sampleKey];
          const updates: Record<string, unknown> = {};
          if (metrics.objectCount !== undefined) updates.objectCount = metrics.objectCount;
          if (metrics.avgLengthMeters !== undefined)
            updates.avgLengthMeters = metrics.avgLengthMeters === null ? deleteField() : metrics.avgLengthMeters;
          if (metrics.avgWidthMeters !== undefined)
            updates.avgWidthMeters = metrics.avgWidthMeters === null ? deleteField() : metrics.avgWidthMeters;
          if (metrics.totalWeightGrams !== undefined)
            updates.totalWeightGrams = metrics.totalWeightGrams === null ? deleteField() : metrics.totalWeightGrams;
          if (metrics.additionalMeasurements !== undefined) {
            Object.assign(
              updates,
              buildAdditionalMeasurementAvgFieldUpdates(metrics.additionalMeasurements, deleteField())
            );
          }
          if (metrics.userDidOverride) updates.userDidOverride = true;
          Object.assign(updates, buildCurrentViewerReviewedUpdates());
          const nextEntry = { ...existingEntry };
          if (metrics.objectCount !== undefined) nextEntry.objectCount = metrics.objectCount;
          if (metrics.userDidOverride) nextEntry.userDidOverride = true;
          if (metrics.avgLengthMeters === null) delete nextEntry.avgLengthMeters;
          else if (metrics.avgLengthMeters !== undefined) nextEntry.avgLengthMeters = metrics.avgLengthMeters;
          if (metrics.avgWidthMeters === null) delete nextEntry.avgWidthMeters;
          else if (metrics.avgWidthMeters !== undefined) nextEntry.avgWidthMeters = metrics.avgWidthMeters;
          if (metrics.totalWeightGrams === null) delete nextEntry.totalWeightGrams;
          else if (metrics.totalWeightGrams !== undefined) nextEntry.totalWeightGrams = metrics.totalWeightGrams;
          if (metrics.additionalMeasurements !== undefined) {
            const nextAdditionalMeasurements = applyAdditionalMeasurementAvgMetrics(
              nextEntry.additionalMeasurements,
              metrics.additionalMeasurements
            );
            if (nextAdditionalMeasurements) nextEntry.additionalMeasurements = nextAdditionalMeasurements;
            else delete nextEntry.additionalMeasurements;
          }
          Object.assign(nextEntry, buildCurrentViewerReviewedUpdates());
          if (nextEntry.analysisStatus === "failed") {
            delete nextEntry.analysisStatus;
            delete nextEntry.analysisError;
          }

          const fieldUpdates = buildNestedSampleFieldUpdates(
            "imageMeasurementEntries",
            currentViewerImage.sampleKey,
            updates
          );
          if (existingEntry?.analysisStatus === "failed") {
            fieldUpdates[`imageMeasurementEntries.${currentViewerImage.sampleKey}.analysisStatus`] = deleteField();
            fieldUpdates[`imageMeasurementEntries.${currentViewerImage.sampleKey}.analysisError`] = deleteField();
          }

          await updateEntryCollection(
            trialId,
            experimentId,
            taskId,
            currentViewerImage.collectionId,
            fieldUpdates as Partial<EntryCollection>
          );

          updateCurrentPlotEntryCollectionsLocally((currentEntryCollectionsForPlot) =>
            currentEntryCollectionsForPlot.map((entryCollection) => {
              if (entryCollection.id !== currentViewerImage.collectionId) return entryCollection;
              const currentEntry = entryCollection.imageMeasurementEntries?.[currentViewerImage.sampleKey];
              if (!currentEntry) return entryCollection;

              return {
                ...entryCollection,
                imageMeasurementEntries: {
                  ...entryCollection.imageMeasurementEntries,
                  [currentViewerImage.sampleKey]: nextEntry,
                },
              };
            })
          );
        }
      }
    },
    [
      buildCurrentViewerReviewedUpdates,
      currentViewerImage,
      displayCollections,
      experimentId,
      taskId,
      currentViewerAnnotationClassNames,
      taskAnnotationClassColorOrder,
      trialId,
      updateCurrentPlotEntryCollectionsLocally,
    ]
  );

  // Early return AFTER all hooks
  if (!plot) return null;

  const renderUnreviewedStatusOverlay = (image: EntryImage, compact = false) => {
    if (!isDeferredAnalysisUnreviewedEntry(image)) return null;

    const canReviewThisImage = canReviewImage(image) && isImageInSelectedTrait(image);
    const canMarkReviewedThisImage = isDeferredAnalysisUnreviewedEntry(image) && canReviewThisImage;
    if (!canMarkReviewedThisImage) return null;

    const buttonPaddingClass = compact ? "px-1.5 py-1" : "px-2 py-1.5";
    const iconSizeClass = compact ? "h-3 w-3" : "h-4 w-4";
    const labelClass = compact ? "text-[9px] leading-none" : "text-[10px] leading-tight";

    return (
      <TooltipProvider>
        <div className="absolute top-1 left-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void setImageReviewStatus(image, "reviewed").catch((error) => {
                    toast({ title: "Review failed", description: error instanceof Error ? error.message : "Please try again.",
                      variant: "destructive" });
                  });
                }}
                className={cn(
                  "flex flex-col items-center gap-0.5 rounded-md bg-white/95 text-foreground shadow-md hover:bg-white transition-colors",
                  buttonPaddingClass
                )}
                title="Mark as reviewed"
                aria-label="Mark as reviewed">
                <CheckCircle2 className={cn(iconSizeClass, "text-foreground")} />
                <span className={cn("font-medium whitespace-nowrap", labelClass)}>Mark as reviewed</span>
              </button>
            </TooltipTrigger>
            <TooltipContent>Mark as reviewed</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    );
  };

  const renderThumbnailActionButtons = (image: EntryImage, compact = false) => {
    if (!shouldRenderAnnotatedImage(image)) return null;

    const isEditableImage =
      canEditRenderedAnnotationImage(image) && isEditableAnnotationImage(image) && isImageInSelectedTrait(image);
    const canEditThisImage = canEditImage(image);
    const buttonPaddingClass = compact ? "p-1.5" : "p-2";
    const iconSizeClass = compact ? "h-3 w-3" : "h-4 w-4";
    const positionClass = compact ? "bottom-1 right-1 gap-1" : "bottom-2 right-2 gap-2";

    return (
      <div className={cn("absolute flex items-center", positionClass)}>
        {isEditableImage &&
          (canEditThisImage ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openImageEditor(image);
              }}
              className={cn("bg-white/90 rounded-full shadow-md hover:bg-white transition-colors", buttonPaddingClass)}
              title="Edit annotations">
              <Pencil className={cn(iconSizeClass, "text-foreground")} />
            </button>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <button
                      type="button"
                      disabled
                      className={cn(
                        "bg-white/90 rounded-full shadow-md opacity-50 cursor-not-allowed",
                        buttonPaddingClass
                      )}
                      title="You can only edit annotations you collected">
                      <Pencil className={cn(iconSizeClass, "text-foreground")} />
                    </button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>You can only edit annotations you collected</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleThumbnailDownload(image, plot.plotID);
          }}
          className={cn("bg-white/90 rounded-full shadow-md hover:bg-white transition-colors", buttonPaddingClass)}
          title="Download annotated image">
          <Download className={cn(iconSizeClass, "text-foreground")} />
        </button>
      </div>
    );
  };

  // Helper function to render entry collection cards
  const renderEntryCollections = (collections: EntryCollection[], traitName?: string) => {
    if (collections.length === 0) {
      return <div className="text-sm text-muted-foreground text-center py-4">No samples for this entry</div>;
    }

    const sorted = [...collections].sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime;
    });

    const hasAnyEntries = sorted.some((collection) => {
      const entries = getFormattedEntries(collection, traitName);
      return entries.length > 0;
    });

    if (!hasAnyEntries) {
      return <div className="text-sm text-muted-foreground text-center py-4">No samples for this entry</div>;
    }

    // Flatten all entries with their collection metadata for grid rendering
    type EntryWithMeta = {
      entry: ReturnType<typeof getFormattedEntries>[0];
      collection: EntryCollection;
      userName: string;
      date: Date | null;
    };

    const allEntriesWithMeta: EntryWithMeta[] = [];
    const nonAnnotatedCollections: {
      collection: EntryCollection;
      entries: ReturnType<typeof getFormattedEntries>;
      userName: string;
      date: Date | null;
    }[] = [];

    sorted.forEach((collection) => {
      const entries = getFormattedEntries(collection, traitName);
      const userName = collection.createdBy?.id ? userNames[collection.createdBy.id] || "Unknown User" : "Unknown User";
      const date = collection.createdAt?.toDate ? collection.createdAt.toDate() : null;

      // Separate annotated image entries from others
      const annotatedEntries = entries.filter(
        (entry) => entry.images.length > 0 && entry.images.some((img) => img.captureMetadataCloudPath)
      );
      const otherEntries = entries.filter(
        (entry) => !(entry.images.length > 0 && entry.images.some((img) => img.captureMetadataCloudPath))
      );

      // Add annotated entries to the flat list for grid rendering
      annotatedEntries.forEach((entry) => {
        allEntriesWithMeta.push({ entry, collection, userName, date });
      });

      // Keep non-annotated entries in their original structure
      if (otherEntries.length > 0) {
        nonAnnotatedCollections.push({ collection, entries: otherEntries, userName, date });
      }
    });

    // Check if any entry in non-annotated collections has images
    const hasAnyImages = nonAnnotatedCollections.some(({ entries }) =>
      entries.some((entry) => entry.images.length > 0)
    );

    return (
      <>
        {/* Grid view for annotated image entries */}
        {allEntriesWithMeta.length > 0 && (
          <div className={`grid gap-4 ${allEntriesWithMeta.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
            {allEntriesWithMeta.map(({ entry, collection, userName, date }) => (
              <div
                key={`${collection.id}-${entry.sampleKey}`}
                className="flex flex-col">
                {/* Flags/Skip badges */}
                {(collection.isFlagged || collection.isSkipped) && (
                  <div className="flex gap-1 mb-2">
                    {collection.isFlagged && (
                      <Badge
                        variant="destructive"
                        className="text-xs">
                        <Flag className="h-3 w-3 mr-1" />
                        Flagged
                      </Badge>
                    )}
                    {collection.isSkipped && (
                      <Badge
                        variant="secondary"
                        className="text-xs">
                        <SkipForward className="h-3 w-3 mr-1" />
                        Skipped
                      </Badge>
                    )}
                  </div>
                )}

                {/* Thumbnail with download */}
                {entry.images.map((img) => (
                  <div
                    key={`${img.url}|${img.uploadStatus || ""}`}
                    className="relative mb-2">
                    <button
                      onClick={() => openImageViewer(img)}
                      className="w-full aspect-square rounded-lg overflow-hidden border border-border hover:border-border transition-colors">
                      {shouldRenderAnnotatedImage(img) ? (
                        <AnnotatedThumbnail
                          key={thumbnailVersions[img.captureMetadataCloudPath] ?? 0}
                          imageUrl={img.url}
                          metadataPath={img.captureMetadataCloudPath}
                          uploadStatus={img.uploadStatus}
                          updatedAt={img.updatedAt}
                          annotationClassNames={
                            annotationClassNamesByEntryConfigId.get(collection.entryConfigRef?.id) ??
                            EMPTY_ANNOTATION_CLASS_NAMES
                          }
                          annotationClassColorOrder={taskAnnotationClassColorOrder}
                          alt={`Sample ${entry.sampleKey}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <StorageImage
                          storagePath={img.url}
                          uploadStatus={img.uploadStatus}
                          updatedAt={img.updatedAt}
                          alt={`Sample ${entry.sampleKey}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </button>
                    {renderUnreviewedStatusOverlay(img)}
                    {renderThumbnailActionButtons(img)}
                  </div>
                ))}

                {/* Value */}
                <div className="text-center">
                  <div className="text-sm font-medium text-foreground">{formatEntryValue(entry.value, entry.type)}</div>
                </div>

                {/* Extracted text from OCR */}
                {entry.extractedImageText && (
                  <ExtractedTextBadge
                    text={entry.extractedImageText}
                    userDidEdit={entry.extractedImageTextUserDidEdit}
                    className="mt-2 rounded-md p-2"
                  />
                )}

                {/* Recorded by and date - directly under sample */}
                <div className="mt-2 text-xs text-muted-foreground text-center space-y-0.5">
                  <div>{userName}</div>
                  {date && (
                    <div className="flex items-center justify-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Non-annotated entries rendered in original style */}
        {nonAnnotatedCollections.map(({ collection, entries, userName, date }, idx) => (
          <div
            key={collection.id || idx}
            className="py-4">
            {(collection.isFlagged || collection.isSkipped) && (
              <div className="pb-2">
                <div className="flex items-start justify-end">
                  <div className="flex gap-1">
                    {collection.isFlagged && (
                      <Badge
                        variant="destructive"
                        className="text-xs">
                        <Flag className="h-3 w-3 mr-1" />
                        Flagged
                      </Badge>
                    )}
                    {collection.isSkipped && (
                      <Badge
                        variant="secondary"
                        className="text-xs">
                        <SkipForward className="h-3 w-3 mr-1" />
                        Skipped
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div>
              <div className="space-y-2 mb-4">
                {entries.map((entry, entryIdx) => (
                  <div
                    key={`${entry.sampleKey}-${entryIdx}`}
                    className="py-2">
                    {/* Stand Count Measurement entries */}
                    {entry.type === ENTRY_CONFIG_TYPES.STAND_COUNT_MEASUREMENT &&
                    entry.value != null &&
                    typeof entry.value === "object" ? (
                      <div className="space-y-3 border border-border rounded-lg p-3">
                        <div className="flex items-center justify-between border-b border-border pb-2">
                          <div className="text-sm font-semibold text-foreground">Sample {entry.sampleKey}</div>
                          {entry.location && (
                            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {entry.location.latitude.toFixed(4)}, {entry.location.longitude.toFixed(4)}
                            </span>
                          )}
                        </div>
                        {entry.images.length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                            {entry.images.map((img) => (
                              <button
                                key={`${img.url}|${img.uploadStatus || ""}`}
                                onClick={() => openImageViewer(img)}
                                className="aspect-square rounded overflow-hidden border border-border hover:border-border transition-colors">
                                <StorageImage
                                  storagePath={img.url}
                                  uploadStatus={img.uploadStatus}
                                  updatedAt={img.updatedAt}
                                  alt={`Sample ${entry.sampleKey}`}
                                  className="w-full h-full object-cover"
                                />
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                          {entry.value.countPerSquareMeter != null ? (
                            <>
                              <span>{formatMetricValue(entry.value.countPerSquareMeter, "countPerM2")} plants/m²</span>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="flex items-center gap-1 cursor-help">
                                      {formatRoundedCountPerSquareMeterPerAcre(entry.value.countPerSquareMeter)} / acre
                                      <Info className="h-3 w-3 opacity-50" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="max-w-48 text-xs">{COUNT_PER_ACRE_TOOLTIP}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </>
                          ) : entry.images.length > 0 ? (
                            <span className="text-amber-600">Awaiting annotation</span>
                          ) : null}
                        </div>
                      </div>
                    ) : entry.type === "rowPlantedStandCount" &&
                      entry.value != null &&
                      typeof entry.value === "object" ? (
                      <div className="space-y-3 border border-border rounded-lg p-3">
                        <div className="flex items-center justify-between border-b border-border pb-2">
                          <div className="text-sm font-semibold text-foreground">Sample {entry.sampleKey}</div>
                          {entry.location && (
                            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {entry.location.latitude.toFixed(4)}, {entry.location.longitude.toFixed(4)}
                            </span>
                          )}
                        </div>
                        {entry.images.length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                            {entry.images.map((img) => (
                              <button
                                key={`${img.url}|${img.uploadStatus || ""}`}
                                onClick={() => openImageViewer(img)}
                                className="aspect-square rounded overflow-hidden border border-border hover:border-border transition-colors">
                                <StorageImage
                                  storagePath={img.url}
                                  uploadStatus={img.uploadStatus}
                                  updatedAt={img.updatedAt}
                                  alt={`Sample ${entry.sampleKey}`}
                                  className="w-full h-full object-cover"
                                />
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          {entry.value.plantsPerMeter != null && (
                            <div className="bg-muted/30 rounded-lg p-2">
                              <div className="text-xs text-muted-foreground">Plants/Meter</div>
                              <div className="text-sm font-semibold text-foreground">
                                {entry.value.plantsPerMeter.toFixed(1)}
                              </div>
                            </div>
                          )}
                          {entry.value.averageSpacingMeters != null && (
                            <div className="bg-muted/30 rounded-lg p-2">
                              <div className="text-xs text-muted-foreground">Avg Spacing</div>
                              <div className="text-sm font-semibold text-foreground">
                                {formatMetricValue(entry.value.averageSpacingMeters * 100, "averageSpacing")}
                              </div>
                            </div>
                          )}
                          {entry.value.averageDiameterMeters != null && (
                            <div className="bg-muted/30 rounded-lg p-2">
                              <div className="text-xs text-muted-foreground">Avg Length</div>
                              <div className="text-sm font-semibold text-foreground">
                                {formatMetricValue(entry.value.averageDiameterMeters * 100, "averageDiameter")}
                              </div>
                            </div>
                          )}
                          {entry.value.averageWidthMeters != null && (
                            <div className="bg-muted/30 rounded-lg p-2">
                              <div className="text-xs text-muted-foreground">Avg Width</div>
                              <div className="text-sm font-semibold text-foreground">
                                {formatMetricValue(entry.value.averageWidthMeters * 100, "averageWidth")}
                              </div>
                            </div>
                          )}
                          {entry.value.averageVari != null && (
                            <div className="bg-muted/30 rounded-lg p-2">
                              <div className="text-xs text-muted-foreground">Avg Greenness</div>
                              <div className="text-sm font-semibold text-foreground">
                                {entry.value.averageVari.toFixed(2)}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                          {entry.value.plantCount != null && <span>Total: {entry.value.plantCount} plants</span>}
                          {entry.value.walkDistanceMeters != null && (
                            <span>Walk: {entry.value.walkDistanceMeters.toFixed(1)}m</span>
                          )}
                          {entry.value.numPlantLines != null && <span>Lines: {entry.value.numPlantLines}</span>}
                        </div>
                      </div>
                    ) : (
                      /* Default rendering for other entry types */
                      <div className="flex items-center gap-3">
                        {hasAnyImages && (
                          <div className="flex gap-1 flex-shrink-0">
                            {entry.images.length > 0 ? (
                              entry.images.map((img) => (
                                <button
                                  key={`${img.url}|${img.uploadStatus || ""}`}
                                  onClick={() => openImageViewer(img)}
                                  className="w-10 h-10 rounded overflow-hidden border border-border hover:border-border transition-colors">
                                  <StorageImage
                                    storagePath={img.url}
                                    uploadStatus={img.uploadStatus}
                                    updatedAt={img.updatedAt}
                                    alt={`Sample ${entry.sampleKey}`}
                                    className="w-full h-full object-cover"
                                  />
                                </button>
                              ))
                            ) : (
                              <div className="w-10 h-10 rounded bg-muted/50 border border-border flex items-center justify-center">
                                <ImageOff className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">
                            {formatEntryValue(entry.value, entry.type)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Sample {entry.sampleKey}
                            {entry.location && (
                              <span className="ml-2 inline-flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {entry.location.latitude.toFixed(4)}, {entry.location.longitude.toFixed(4)}
                              </span>
                            )}
                          </div>
                          {entry.extractedImageText && (
                            <ExtractedTextBadge
                              text={entry.extractedImageText}
                              userDidEdit={entry.extractedImageTextUserDidEdit}
                              lineClamp={2}
                              className="mt-1"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* User and Date info at bottom */}
              <div className="pt-2 text-xs text-muted-foreground space-y-1">
                <div>
                  <span className="font-medium">Recorded by:</span> {userName}
                </div>
                {date && (
                  <div className="flex items-center gap-1">
                    <span className="font-medium">Date:</span>
                    <Calendar className="h-3 w-3" />
                    {date.toLocaleDateString()} {date.toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </>
    );
  };

  // Determine if we should show navigation (more than one plot)
  const showNavigation = navigation && navigation.totalPlots > 1;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onClose}>
        <DialogContent
          aria-describedby={undefined}
          className="max-w-4xl max-h-[90vh] p-0 gap-0 overflow-hidden">
          <div className="flex items-stretch min-h-0">
            {/* Left Navigation Arrow */}
            {showNavigation && (
              <div className="flex-shrink-0 flex items-center justify-center w-16">
                <Button
                  variant="outline"
                  size="icon"
                  className={cn(
                    "h-12 w-12 rounded-full border-2 shadow-sm transition-all",
                    navigation.canGoPrev
                      ? "hover:bg-muted hover:scale-105 border-border"
                      : "opacity-30 cursor-not-allowed border-border/50"
                  )}
                  disabled={!navigation.canGoPrev}
                  onClick={navigation.goToPrev}
                  title="Previous plot (←)">
                  <ChevronLeft className="h-6 w-6" />
                  <span className="sr-only">Previous plot</span>
                </Button>
              </div>
            )}

            {/* Main Content */}
            <div className="flex-1 min-w-0 flex flex-col max-h-[90vh]">
              <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <DialogTitle className="text-xl">Plot {plot.plotID}</DialogTitle>
                    <div className="text-sm text-muted-foreground">
                      {plot.row && plot.column && (
                        <span>
                          Row {plot.row}, Column {plot.column}
                        </span>
                      )}
                    </div>
                    {plotMetadataEntries.length > 0 && (
                      // This list is uncapped on purpose - it is where the heatmap
                      // tooltip's "+N more" sends the reader - so it has to bound
                      // itself. It sits in the dialog's `flex-shrink-0` header,
                      // above a `flex-1 overflow-y-auto` body, so an unbounded list
                      // would shrink that body toward zero and push the trait
                      // summary and entries out of reach. Scrolling the list keeps
                      // every field reachable without stealing the body's height.
                      <dl
                        data-testid="plot-details-metadata"
                        className="mt-2 flex max-h-[20vh] flex-wrap gap-x-4 gap-y-1 overflow-y-auto text-sm">
                        {plotMetadataEntries.map((entry) => (
                          <div
                            key={entry.label}
                            className="flex min-w-0 items-baseline gap-1.5">
                            <dt
                              data-testid="plot-details-metadata-label"
                              className="text-muted-foreground">
                              {entry.label}
                            </dt>
                            {/* A single long unbroken value (URL, sample code) would
                                otherwise overflow the `overflow-hidden` dialog with
                                no way to scroll to it. The tooltip guards this too. */}
                            <dd className="break-words font-medium">{entry.value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>

                  {/* Position indicator */}
                  {showNavigation && (
                    <div className="text-sm text-muted-foreground bg-muted/50 px-3 py-1 rounded-full">
                      {navigation.currentIndex + 1} of {navigation.totalPlots}
                    </div>
                  )}
                </div>
              </DialogHeader>

              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                <div className="space-y-4 py-4">
                  {/* Summary Stats */}
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-3">{selectedTraitName} Summary</h3>
                    <div className={`grid gap-3 ${isNumericType ? "grid-cols-2" : "grid-cols-1"}`}>
                      {isNumericType && (
                        <div className="text-center py-4 bg-muted/30 rounded-lg">
                          <div className="text-sm font-medium text-foreground mb-1">Average</div>
                          <div className="text-2xl font-bold text-foreground">
                            {displayValue !== null && displayValue !== undefined
                              ? typeof displayValue === "number"
                                ? displayValue.toFixed(1)
                                : displayValue
                              : "N/A"}
                          </div>
                        </div>
                      )}
                      <div className="text-center py-4 bg-muted/30 rounded-lg">
                        <div className="text-sm font-medium text-foreground mb-1">
                          {totalSamples === 1 ? "Sample" : "Samples"}
                        </div>
                        <div className="text-2xl font-bold text-foreground">{totalSamples}</div>
                      </div>
                    </div>
                  </div>

                  {/* Selected Trait Entry Collections */}
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                      {selectedTraitConfig &&
                        (() => {
                          const Icon = getTraitIcon(selectedTraitConfig.type);
                          const color = getTraitColor(selectedTraitConfig.type);
                          return (
                            <Icon
                              className="h-5 w-5"
                              style={{ color }}
                            />
                          );
                        })()}
                      {selectedTraitName}
                    </h3>
                    <div className="space-y-4">
                      {renderEntryCollections(selectedTraitCollections, selectedTraitName)}
                    </div>
                  </div>

                  {/* Other Traits */}
                  {otherTraits.length > 0 && (
                    <div className="mt-12">
                      <h3 className="text-base font-semibold text-foreground mb-3">Other Traits</h3>
                      <Accordion
                        type="multiple"
                        className="w-full">
                        {otherTraits.map((trait) => {
                          const collections = collectionsByTrait[trait.id || ""] || [];
                          const { sampleCount, collectionCount } = getTraitStats(displayCollections, trait.id || "");

                          const Icon = getTraitIcon(trait.type);
                          const color = getTraitColor(trait.type);

                          return (
                            <AccordionItem
                              key={trait.id}
                              value={trait.id || ""}>
                              <AccordionTrigger className="text-sm">
                                <div className="flex items-center gap-2">
                                  <Icon
                                    className="h-4 w-4"
                                    style={{ color }}
                                  />
                                  <span>{trait.name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    ({collectionCount} {collectionCount === 1 ? "entry" : "entries"})
                                  </span>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="space-y-4 pt-2">{renderEntryCollections(collections, trait.name)}</div>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </div>
                  )}

                  {/* Transcriptions */}
                  <div className="pt-8">
                    <h3 className="text-base font-semibold text-foreground mb-2">Transcriptions</h3>
                    {transcriptionSamples.length === 0 ? (
                      <div className="text-sm text-muted-foreground">No transcriptions for this plot.</div>
                    ) : (
                      <div className="space-y-3">
                        {transcriptionSamples.map((sample, index) => {
                          const playKey = `${sample.sampleKey}-${sample.createdById ?? index}-${sample.cloudAudioPath || sample.sampleKey}`;
                          const isPlaying = transcriptionPlayingKey === playKey;
                          const isLoading = transcriptionLoadingKey === playKey;
                          const showError = transcriptionErrorKey === playKey;
                          const userLabel = sample.createdById
                            ? userNames[sample.createdById] || "Unknown User"
                            : "Unknown User";

                          return (
                            <div
                              key={playKey}
                              className="border rounded-md p-3 flex flex-col gap-2">
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-semibold text-foreground">{userLabel}</div>
                                <button
                                  onClick={() => handlePlayTranscription(sample.sampleKey, sample.cloudAudioPath)}
                                  disabled={!sample.cloudAudioPath || isLoading}
                                  className="inline-flex items-center px-3 py-1.5 text-xs rounded bg-agri-green text-white hover:bg-agri-green-dark disabled:bg-gray-300 disabled:text-muted-foreground">
                                  {isLoading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : isPlaying ? (
                                    <span className="flex items-center gap-1">
                                      <Pause className="h-4 w-4" />
                                      <span className="w-2 h-2 rounded-full bg-white/80 animate-pulse inline-block" />
                                    </span>
                                  ) : (
                                    <Play className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatTimestampReadable(sample.timestamp)}
                              </div>
                              <div className="text-sm text-foreground whitespace-pre-line">
                                {sample.text || "No text"}
                              </div>
                              {showError && <div className="text-xs text-red-600">Unable to play this audio</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Image Gallery */}
                  {allImages.length > 0 && (
                    <TooltipProvider>
                      <div className="mt-12">
                        <h3 className="text-base font-semibold text-foreground pt-4 mb-3">
                          All Images ({allImages.length})
                        </h3>
                        <div className="grid grid-cols-3 gap-2">
                          {allImages.map((img, idx) => (
                            <div
                              key={`${img.url}|${img.uploadStatus || ""}`}
                              className="relative">
                              <button
                                onClick={() => openImageViewer(img)}
                                className="w-full aspect-square rounded overflow-hidden border border-border hover:border-border transition-colors">
                                {shouldRenderAnnotatedImage(img) ? (
                                  <AnnotatedThumbnail
                                    key={thumbnailVersions[img.captureMetadataCloudPath] ?? 0}
                                    imageUrl={img.url}
                                    metadataPath={img.captureMetadataCloudPath}
                                    uploadStatus={img.uploadStatus}
                                    updatedAt={img.updatedAt}
                                    annotationClassNames={
                                      annotationClassNamesByEntryConfigId.get(
                                        collectionTraitIdsByCollectionId.get(img.collectionId ?? "") ?? ""
                                      ) ?? EMPTY_ANNOTATION_CLASS_NAMES
                                    }
                                    annotationClassColorOrder={taskAnnotationClassColorOrder}
                                    alt={`Image ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <StorageImage
                                    storagePath={img.url}
                                    uploadStatus={img.uploadStatus}
                                    updatedAt={img.updatedAt}
                                    alt={`Image ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </button>
                              {/* Extracted text badge overlay */}
                              {img.extractedImageText && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div
                                      className={cn(
                                        "absolute left-1 p-1 bg-white/90 rounded-full shadow-sm",
                                        isDeferredAnalysisUnreviewedEntry(img) ? "top-12" : "top-1"
                                      )}>
                                      <FileText className="h-3 w-3 text-muted-foreground" />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="bottom"
                                    className="max-w-xs">
                                    <p className="text-xs">{img.extractedImageText}</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {renderUnreviewedStatusOverlay(img, true)}
                              {renderThumbnailActionButtons(img, true)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            </div>

            {/* Right Navigation Arrow */}
            {showNavigation && (
              <div className="flex-shrink-0 flex items-center justify-center w-16">
                <Button
                  variant="outline"
                  size="icon"
                  className={cn(
                    "h-12 w-12 rounded-full border-2 shadow-sm transition-all",
                    navigation.canGoNext
                      ? "hover:bg-muted hover:scale-105 border-border"
                      : "opacity-30 cursor-not-allowed border-border/50"
                  )}
                  disabled={!navigation.canGoNext}
                  onClick={navigation.goToNext}
                  title="Next plot (→)">
                  <ChevronRight className="h-6 w-6" />
                  <span className="sr-only">Next plot</span>
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox with AnnotatedImageViewer */}
      <Dialog
        open={!!viewerSession}
        onOpenChange={(open) => {
          if (!open) {
            if (sequenceNavigationSaveOverlay) {
              return;
            }
            if (viewerHasUnsavedChanges) {
              unsavedChangesDialogTrigger.current = "dialog-close";
              setUnsavedChangesDialogState({ kind: "discard-close" });
              return;
            }
            closeImageViewer();
          }
        }}>
        <DialogContent
          aria-describedby={undefined}
          onEscapeKeyDown={(event) => {
            // While editing annotations, Escape must NOT close the viewer — it deselects
            // the selected box (handled inside the editor) or does nothing. Reviewers were
            // losing their edit session by fat-fingering Escape; the X button closes it.
            if (isViewerEditMode) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            // Same rule as Escape, and the one reviewers actually hit: in edit mode the content is
            // 98vw x 95vh, so "outside" is a ~20px frame around a full-bleed image canvas that
            // reviewers clip by accident, losing the session mid-sweep. The X button closes.
            if (isViewerEditMode) event.preventDefault();
          }}
          className={
            isViewerEditMode
              ? "max-w-[98vw] max-h-[99vh] overflow-hidden p-0"
              : "max-w-6xl max-h-[95vh] overflow-hidden p-0"
          }>
          <DialogTitle className="sr-only">
            {currentSequenceImage
              ? `Plot ${currentSequenceImage.plotId} image viewer`
              : currentViewerImage
                ? "Image viewer"
                : "Loading image viewer"}
          </DialogTitle>
          {isLoadingCrossPlotImageNavigation ? (
            <div className="flex flex-col items-center justify-center h-[60vh] gap-3 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="text-sm">Loading next image...</span>
            </div>
          ) : currentViewerImage ? (
            <div className={`flex flex-col ${isViewerEditMode ? "h-[95vh]" : "h-[85vh]"}`}>
              {showImageSequenceNavigation && currentSequenceImage && (
                <div className="flex-shrink-0 border-b border-border bg-background px-4 py-3 pr-12">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                    <div className="min-w-0 flex-1 basis-80">
                      <div className="text-sm font-semibold text-foreground">Plot {currentSequenceImage.plotId}</div>
                      <div className="text-sm text-muted-foreground flex flex-wrap gap-3">
                        <span>Trait: {currentSequenceImage.traitName || selectedTraitName}</span>
                        <span>Collected by: {currentSequenceImage.userEmail}</span>
                        <span>Sample {currentSequenceImage.displaySampleKey ?? currentSequenceImage.sampleKey}</span>
                        {isReviewPriority(currentSequenceImage.reviewPriority) && (
                          <span title="Review order. Priority 1 comes first.">Priority {currentSequenceImage.reviewPriority}</span>
                        )}
                        {currentViewerImageIsUnreviewedDeferred && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 text-amber-700">
                                  <span className="font-bold">*</span>
                                  Unreviewed
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>Analysis result has not been reviewed</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      <div className="flex items-center gap-3 whitespace-nowrap tabular-nums">
                        <span>
                          Image {currentSequenceImageIndex + 1} of {allNavigableImages.length}
                        </span>
                        {rankedReviewImages.length > 0 && (
                          <span className="border-l border-border pl-3">
                            {rankedReviewedCount}/{rankedReviewImages.length} reviewed
                          </span>
                        )}
                      </div>
                      {rankedReviewImages.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!!resumeQueueDisabledReason}
                          title={resumeQueueDisabledReason ?? "Go to the first unreviewed image in priority order"}
                          onClick={() => {
                            if (!firstUnreviewedImage) return;
                            // Resume changes the preview only. It must not confirm the current image.
                            navigateToSequenceImage({
                              plotId: firstUnreviewedImage.plotId,
                              itemKey: firstUnreviewedImage.key,
                              direction: "next",
                              viewerMode: "preview",
                            });
                          }}>
                          First unreviewed
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-1 min-h-0">
                {showImageSequenceNavigation && (
                  <div className="flex-shrink-0 flex items-center justify-center w-16">
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn(
                        "h-12 w-12 rounded-full border-2 shadow-sm transition-all",
                        previousSequenceNavigationTarget && !sequenceNavigationSaveOverlay && !pendingPersistDirection
                          ? "hover:bg-muted hover:scale-105 border-border"
                          : "opacity-30 cursor-not-allowed border-border/50"
                      )}
                      disabled={
                        !previousSequenceNavigationTarget ||
                        !!sequenceNavigationSaveOverlay ||
                        !!pendingPersistDirection ||
                        !!endOfObjectsPrompt
                      }
                      // The chevrons stay pure image navigation — the escape hatch for skipping an
                      // image without stepping every object — so they no longer advertise the arrow keys.
                      onClick={() => void goToAdjacentSequenceImage("prev")}
                      title="Previous image">
                      <ChevronLeft className="h-6 w-6" />
                      <span className="sr-only">Previous image</span>
                    </Button>
                  </div>
                )}

                <div className="flex-1 min-w-0 flex flex-col">
                  <div className="flex-1 min-h-0">
                    <AnnotatedImageViewer
                      ref={viewerRef}
                      key={
                        viewerSession?.kind === "sequence"
                          ? (currentSequenceImage?.key ?? pendingCrossPlotImageNavigation?.itemKey)
                          : `${currentViewerImage.url}|${currentViewerImage.updatedAt?.seconds ?? "none"}`
                      }
                      imageUrl={currentViewerImage.url}
                      dataCollectionOnly={currentViewerImage.dataCollectionOnly}
                      metadataPath={currentViewerMetadataPath}
                      uploadStatus={currentViewerImage.uploadStatus}
                      updatedAt={currentViewerImage.updatedAt}
                      downloadFilename={`${plot.plotID}${currentViewerImage.traitName ? `--${sanitizeFilename(currentViewerImage.traitName)}` : ""}--sample${currentViewerImage.sampleKey}.jpg`}
                      displayOptions={displayOptions}
                      onDisplayOptionsChange={setDisplayOptions}
                      viewerMode={viewerSession?.kind === "sequence" ? viewerSession.viewerMode : "preview"}
                      onEditModeChange={(isEditMode) => {
                        setViewerSession((currentSession) =>
                          currentSession?.kind === "sequence"
                            ? {
                                ...currentSession,
                                viewerMode: isEditMode ? "edit" : "preview",
                              }
                            : currentSession
                        );
                      }}
                      onUnsavedChangesChange={setViewerHasUnsavedChanges}
                      canEdit={viewerCanEdit}
                      canReview={viewerCanReview}
                      isEditable={currentViewerImageIsEditableForSelectedTrait}
                      supportsOutsideBoundaryAnnotations={supportsOutsideBoundaryAnnotationsForEntryConfig(
                        currentViewerEntryConfig
                      )}
                      entryPath={viewerEntryPath}
                      // Explicit null = the entry was consulted and has no depth file, which is
                      // what unlocks 2D landmark editing; undefined would keep it locked.
                      entryDepthCloudPath={currentViewerImage ? (currentViewerImage.depthCloudPath ?? null) : undefined}
                      annotationTypePreference={currentViewerAnnotationTypePreference}
                      containmentParams={currentViewerContainmentParams}
                      measurementDescriptors={currentViewerMeasurementDescriptors}
                      annotationClassNames={currentViewerAnnotationClassNames}
                      annotationClassColorOrder={taskAnnotationClassColorOrder}
                      annotationClassStateKey={
                        selectedOrganization && trialId && experimentId && taskId
                          ? `${selectedOrganization.id}/${trialId}/${experimentId}/${taskId}`
                          : undefined
                      }
                      isReviewableUnreviewed={currentViewerImageIsUnreviewedDeferred}
                      isReviewableReviewed={currentViewerImageIsReviewedDeferred}
                      onMarkReviewed={markCurrentViewerImageReviewed}
                      onMarkUnreviewed={markCurrentViewerImageUnreviewed}
                      onSave={handleViewerSave}
                      initialViewSettings={
                        viewerSession?.kind === "sequence" && viewSettingsSession.choice === "persist"
                          ? viewSettingsSession.settings
                          : null
                      }
                      onViewSettingsChange={
                        viewerSession?.kind === "sequence" ? handleViewerViewSettingsChange : undefined
                      }
                      initialReviewObjectEdge={reviewLandingEdge}
                    />
                  </div>
                  {currentViewerImage.extractedImageText && (
                    <div className="flex-shrink-0 border-t border-border bg-muted/30 px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1">
                        <FileText className="h-4 w-4" />
                        <span className="font-medium">Text Extracted From Image</span>
                        {currentViewerImage.extractedImageTextUserDidEdit && (
                          <span className="text-xs italic">(edited)</span>
                        )}
                      </div>
                      <div className="text-sm text-foreground whitespace-pre-wrap break-words max-h-24 overflow-y-auto">
                        {currentViewerImage.extractedImageText}
                      </div>
                    </div>
                  )}
                </div>

                {showImageSequenceNavigation && (
                  <div className="flex-shrink-0 flex items-center justify-center w-16">
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn(
                        "h-12 w-12 rounded-full border-2 shadow-sm transition-all",
                        nextSequenceNavigationTarget && !sequenceNavigationSaveOverlay && !pendingPersistDirection
                          ? "hover:bg-muted hover:scale-105 border-border"
                          : "opacity-30 cursor-not-allowed border-border/50"
                      )}
                      disabled={
                        !nextSequenceNavigationTarget ||
                        !!sequenceNavigationSaveOverlay ||
                        !!pendingPersistDirection ||
                        !!endOfObjectsPrompt
                      }
                      onClick={() => void goToAdjacentSequenceImage("next")}
                      title="Next image">
                      <ChevronRight className="h-6 w-6" />
                      <span className="sr-only">Next image</span>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={unsavedChangesDialogState !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setUnsavedChangesDialogState(null);
          }
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {aggregatePersistenceNeedsRetry
                ? "Counts still need saving"
                : unsavedChangesDialogState?.kind === "save-failed"
                  ? "Save failed"
                  : "Discard unsaved changes?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {aggregatePersistenceNeedsRetry
                ? "Your annotations were saved, but their counts and density were not. Stay on this image and try saving again so the annotation data and Firestore totals cannot diverge."
                : unsavedChangesDialogState?.kind === "save-failed"
                  ? "We couldn't save your annotation changes. You can keep editing or discard the unsaved changes and continue."
                  : "You have unsaved annotation changes. Closing now will discard them."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {aggregatePersistenceNeedsRetry
                ? "Stay on image"
                : unsavedChangesDialogState?.kind === "save-failed"
                  ? "OK"
                  : "Keep editing"}
            </AlertDialogCancel>
            {!aggregatePersistenceNeedsRetry && (
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={(event) => {
                  if (viewerRef.current?.discardUnsavedChanges() === false) {
                    event.preventDefault();
                    // The sidecar can finish after this dialog renders. Refresh
                    // from the imperative guard so the dialog explains why the
                    // now-partial save cannot be discarded.
                    setUnsavedChangesDialogState((current) => (current === null ? null : { ...current }));
                    return;
                  }
                  if (unsavedChangesDialogState?.kind === "save-failed") {
                    setViewerHasUnsavedChanges(false);
                    navigateToSequenceImage(unsavedChangesDialogState.navigationTarget);
                    return;
                  }

                  if (unsavedChangesDialogTrigger.current === "back-button") {
                    window.history.back();
                  }
                  closeImageViewer();
                }}>
                {unsavedChangesDialogState?.kind === "save-failed" ? "Discard & continue" : "Discard changes"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PersistViewSettingsDialog
        open={pendingPersistDirection !== null}
        changeLines={
          pendingPersistDirection && latestViewSettingsRef.current
            ? describeChangedViewSettings(latestViewSettingsRef.current, (key) =>
                resolveMeasurementAxisLabel(key, currentViewerMeasurementDescriptors)
              )
            : []
        }
        onOpenChange={(isOpen) => {
          if (!isOpen) setPendingPersistDirection(null);
        }}
        onKeep={() => resolvePersistChoice("persist")}
        onReset={() => resolvePersistChoice("reset")}
      />

      <ReviewObjectsCompleteDialog
        open={endOfObjectsPrompt !== null}
        targetScope={endOfObjectsPrompt?.scope ?? "image"}
        plotId={endOfObjectsPrompt?.plotId}
        dontAskAgain={endOfObjectsPrompt?.dontAskAgain ?? false}
        onDontAskAgainChange={(value) =>
          setEndOfObjectsPrompt((previous) => (previous ? { ...previous, dontAskAgain: value } : previous))
        }
        onOpenChange={(isOpen) => {
          if (!isOpen) setEndOfObjectsPrompt(null);
        }}
        onConfirm={confirmEndOfObjects}
        onCancel={() => setEndOfObjectsPrompt(null)}
      />

      <TransitionOverlay
        open={!!sequenceNavigationSaveOverlay}
        title={sequenceNavigationSaveOverlay?.title ?? ""}
        subtitle={sequenceNavigationSaveOverlay?.subtitle}
      />
    </>
  );
};

export default PlotDetailsSheet;
